/* eslint-disable no-useless-rename */
// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define, no-shadow, max-len */
import { CancellationError } from 'cancel';
import { v4 as uuid } from 'uuid';
import EventEmitter from 'events';
import assign from 'lodash/assign';
import defaults from 'lodash/defaults';
import get from 'lodash/get';
import merge from 'lodash/merge';
import once from 'lodash/once';
import values from 'lodash/values';
import debounce from 'lodash/debounce';
import ResizeObserverPolyfillDefault from 'resize-observer-polyfill';
import queue from 'async/queue';

import memoizeLast from '../../helpers/memoizeLast';
import eventing from '../../helpers/eventing';
import createLogger from '../../helpers/log';
import eventHelper from '../../helpers/eventHelper';
import envDefault from '../../helpers/env';
import getNetworkStatus from '../../helpers/networkStatus.js';
import createAmrStateMachine from '../../helpers/amr/amrStateMachine';
import createActorStatsHelper from '../../helpers/actorStatsHelper';
import castToBoolean from '../../helpers/castToBoolean';
import createConnectivityAttemptPinger from '../../helpers/connectivityAttemptPinger';
import createConnectivityState from '../../helpers/connectivityState';
import ExceptionCodesDefault from '../exception_codes';
import OTErrorClassDefault from '../ot_error_class';
import waitUntilFactory from '../../helpers/waitUntil';
import eventNames from '../../helpers/eventNames';
import isGetRtcStatsReportSupported from '../../helpers/isGetRtcStatsReportSupported';
import otErrorFactory from '../../helpers/otError';
import overallTimeout from '../../helpers/overallTimeout';
import Errors from '../Errors';
import now from '../../helpers/now';
import RafRunner from '../RafRunner';
import interpretSubscriberCreateError from './interpretSubscriberCreateError';
import createVideoQualityWatcherDefault, { VIDEO_QUALITY_WATCHER_INTERVAL_FAST, VIDEO_QUALITY_WATCHER_INTERVAL_SLOW } from '../../helpers/videoQualityWatcher';
import createSubscriberVideoQualityChangedChecker from './subscriberVideoQualityChangedChecker.js';
import WidgetViewFactory from '../../helpers/widget_view';
import audioLevelBehaviour from './audioLevelBehaviour';
import AudioLevelMeterDefault from '../chrome/audio_level_meter';
import AudioLevelTransformerDefault from '../audio_level_transformer';
import BackingBarDefault from '../chrome/backing_bar';
import ChromeDefault from '../chrome/chrome';
import EventsFactory from '../events';
import getStatsHelpersDefault from '../peer_connection/get_stats_helpers';
import hasAudioOutputLevelStatCapabilityDefault from '../../helpers/hasAudioOutputLevelStatCapability';
import hasRemoteStreamsWithWebAudioDefault from '../../helpers/hasRemoteStreamsWithWebAudio';
import audioLevelSamplerFactoryDefault from './audioLevelSampler';
import interpretPeerConnectionErrorFactory from '../interpretPeerConnectionError';
import MuteButtonFactory from '../chrome/mute_button';
import NamePanelDefault from '../chrome/name_panel';
import OTHelpersDefault from '../../common-js-helpers/OTHelpers';
import StylableComponentDefault from '../styling/stylable_component';
import createWindowMock from '../../helpers/createWindowMock';
import PeerConnectionFactory from '../peer_connection/peer_connection';
import SubscriberPeerConnectionFactory from '../peer_connection/subscriber_peer_connection';
import SubscribingStateDefault from './state';
import VideoDisabledIndicatorDefault from '../chrome/video_disabled_indicator';
import AudioBlockedIndicatorDefault from '../chrome/AudioBlockedIndicator';
import VideoUnsupportedIndicatorDefault from '../chrome/VideoUnsupportedIndicator';
import createSendMethodDefault from '../session/createSendMethod';
import { parseIceServers as parseIceServersDefault } from '../../RaptorSession/raptor/parseIceServers';
import getMediaModeBySourceStreamId from '../../helpers/get-media-mode-by-source-stream-id';
import createAudioConnectorDefault from './audioConnector';
import MediaProcessorConnector from '../mediaProcessorConnector/mediaProcessorConnector';
import { isAmrVideoBufferError } from '../../helpers/amr/amrVideoBufferError';
import updateTrackOnSingleStream from '../../helpers/updateTrackOnStream';
import ONE_SECOND_IN_MS from '../../common-js-helpers/time';
import amrStates from '../../helpers/amr/amrStates';
import createMOSScoreHelperDefault from '../../helpers/MOSScoreHelper';
import STATS_SOURCES from '../peer_connection/statsSources';
import createNetworkConditionChecker from './networkConditionChecker';
import { validateLanguageCode } from '../../helpers/languageCodes';
import setDeprecatedProperty from '../../helpers/setDeprecatedProperty';
import { addToPending, executePending, clearPending } from '../../helpers/pendingCalls.js';
import createRateLimitedRunnerDefault from '../../helpers/rateLimitedRunner.js';

const defaultWidgetView = WidgetViewFactory();
const EventsDefault = EventsFactory();
const errorsDefault = Errors;
const interpretPeerConnectionErrorDefault = interpretPeerConnectionErrorFactory();
const MuteButtonDefault = MuteButtonFactory();
const windowMock = createWindowMock(global);

const PeerConnection = PeerConnectionFactory({
  global: windowMock,
});
const SubscriberPeerConnectionDefault = SubscriberPeerConnectionFactory({
  PeerConnection,
});

const loggingDefault = createLogger('Subscriber');
const otErrorDefault = otErrorFactory();
const DEFAULT_AUDIO_VOLUME = 100;

const isLocalStream = (stream, session) => stream.connection.id === session.connection.id;

const constructSubscriberUri = ({ apiKey, sessionId, streamId, subscriberId }) => [
  '/v2/partner', apiKey, 'session', sessionId, 'stream', streamId, 'subscriber', subscriberId,
].join('/');

const waitUntil = waitUntilFactory();

function normalizeAudioVolume(volume) {
  return Math.max(0, Math.min(100, parseInt(volume, 10)));
}

function determineAudioVolume(props) {
  if (props.audioVolume !== undefined) {
    return props.audioVolume;
  } else if (props.subscribeToAudio === false) {
    return 0;
  }
  return DEFAULT_AUDIO_VOLUME;
}

export default function SubscriberFactory({
  AudioLevelMeter = AudioLevelMeterDefault,
  AudioLevelTransformer = AudioLevelTransformerDefault,
  BackingBar = BackingBarDefault,
  Chrome = ChromeDefault,
  env = envDefault,
  Errors: errors = errorsDefault,
  Events = EventsDefault,
  ExceptionCodes = ExceptionCodesDefault,
  audioLevelSamplerFactory = audioLevelSamplerFactoryDefault,
  getStatsHelpers = getStatsHelpersDefault,
  hasAudioOutputLevelStatCapability = hasAudioOutputLevelStatCapabilityDefault,
  hasRemoteStreamsWithWebAudio = hasRemoteStreamsWithWebAudioDefault,
  interpretPeerConnectionError = interpretPeerConnectionErrorDefault,
  logging = loggingDefault,
  MuteButton = MuteButtonDefault,
  NamePanel = NamePanelDefault,
  otError = otErrorDefault,
  OTErrorClass = OTErrorClassDefault,
  OTHelpers = OTHelpersDefault,
  StylableComponent = StylableComponentDefault,
  SubscriberPeerConnection = SubscriberPeerConnectionDefault,
  SubscribingState = SubscribingStateDefault,
  VideoDisabledIndicator = VideoDisabledIndicatorDefault,
  AudioBlockedIndicator = AudioBlockedIndicatorDefault,
  VideoUnsupportedIndicator = VideoUnsupportedIndicatorDefault,
  createSendMethod = createSendMethodDefault,
  parseIceServers = parseIceServersDefault,
  createVideoQualityWatcher = createVideoQualityWatcherDefault,
  document = global.document,
  WidgetView = defaultWidgetView,
  createAudioConnector = createAudioConnectorDefault,
  ResizeObserverPolyfill = ResizeObserverPolyfillDefault,
  MediaStream = global.MediaStream,
  AMRStateDurations,
  createMOSScoreHelper = createMOSScoreHelperDefault,
  createRateLimitedRunner = createRateLimitedRunnerDefault,
  ...deps
} = {}) {
  const BIND_VIDEO_DELAY_MAX = 30000;
  const BIND_VIDEO_DELAY_WARNING = 15000;
  const RESIZE_DEBOUNCE_WAIT = deps.RESIZE_DEBOUNCE_WAIT || ONE_SECOND_IN_MS;

  /**
   * The Subscriber object is a representation of the local video element that is playing back
   * a remote stream. The Subscriber object includes methods that let you disable and enable
   * local audio playback for the subscribed stream. The <code>subscribe()</code> method of the
   * {@link Session} object returns a Subscriber object.
   *
   * @property {Element} element The HTML DOM element containing the Subscriber.
   * @property {String} id The DOM ID of the Subscriber.
   * @property {Stream} stream The stream to which you are subscribing.
   *
   * @class Subscriber
   * @augments EventDispatcher
   */
  const Subscriber = function Subscriber(targetElement, options = {}, completionHandler = () => { }) {
    if (options.analytics === undefined) {
      // @todo does anyone instantiate a Subscriber outside of `session.subscribe`?
      // it might be best to instantiate an analytics object and replace it on subscribe like
      // we do in the publisher.
      throw new Error('Subscriber requires an instance of analytics');
    }

    /** @type AnalyticsHelperDefault */
    const analytics = options.analytics;
    const _widgetId = uuid();
    const _audioLevelCapable = Subscriber.hasAudioOutputLevelStatCapability() || hasRemoteStreamsWithWebAudio();
    const _subscriber = this;
    const _pcConnected = {};
    const peerConnectionsAsync = {};
    const webRTCStreams = {};
    const hybridSessionTransitionStartTimes = {};
    const _peerConnectionEvents = {};
    const updateTrackOnStream = (mediaStream, oldTrack, newTrack) => {
      if (_exposedWebRTCStream) {
        updateTrackOnSingleStream(_exposedWebRTCStream, oldTrack, newTrack);
      }
      updateTrackOnSingleStream(mediaStream, oldTrack, newTrack);
    };
    const _audioConnector = createAudioConnector({ updateTrackOnStream });

    /** @type {defaultWidgetView|null} */
    let _widgetView;
    let _chrome;
    let _muteDisplayMode;
    let _audioLevelMeter;
    let _subscribeStartTime;
    let _state;
    let _audioLevelSampler;
    let _audioLevelBehaviour;
    let _webRTCStream;
    let _videoEnabled;
    let _lastSubscribeToVideoReason;
    let _attemptStartTime;
    /** @type IntervalRunnerDefault | undefined */
    let _streamEventHandlers;
    let _isSubscribingToAudio;
    let _loaded = false;
    let _domId = targetElement || _widgetId;
    let _session = options.session;
    let _stream = options.stream;
    const _shouldUseSpc = _session._singlePeerConnectionController !== undefined;
    let _audioVolume;
    let _latestPositiveVolume;
    let _frameRateRestricted = false;
    let _preDisconnectStats = {};
    let _congestionLevel = null;
    let _hasLoadedAtLeastOnce = false;
    let _isVideoSupported = true;
    let fallbackIceServers = [];
    let _properties = defaults({}, options, {
      showControls: true,
      testNetwork: false,
      fitMode: _stream.defaultFitMode || 'cover',
      insertDefaultUI: true,
      initials: _stream.initials,
    });
    let _isVideoManuallyMuted;
    let _activeSourceStreamId;
    const _isAdaptiveEnabled = _session.sessionInfo.isAdaptiveEnabled;
    const _lastConnectionStatesMap = {
      P2P: eventNames.SUBSCRIBER_DISCONNECTED,
      MANTIS: eventNames.SUBSCRIBER_DISCONNECTED,
    };
    let _subscriberConnectionState = eventNames.SUBSCRIBER_DISCONNECTED;
    let _videoMediaProcessorConnector;
    let _originalVideoTrack;
    let _resizeObserver;
    let _exposedWebRTCStream;
    let _migrationTimeoutId;
    let _previousPeerConnection;
    let _pendingSetAudioMediaProcessorConnector;
    let _networkConditionStats = {};
    let _senderStats = {};
    let _senderQualityRestrictions = {};

    const statsHelpers = getStatsHelpers();
    const networkConditionChecker = createNetworkConditionChecker({ session: _session, subscriberId: _widgetId, stream: _stream });
    networkConditionChecker.on('networkConditionStatsChanged', ({ networkConditionStats, networkConditionChanged }) => {
      ({ senderStats: _senderStats, senderQualityRestrictions: _senderQualityRestrictions, ..._networkConditionStats } = networkConditionStats);
      if (networkConditionChanged) {
        _getStats(STATS_SOURCES.SUBSCRIBER_NETWORK_CONDITION_CHECKER, (error, stats) => {
          if (error) {
            return;
          }
          this.trigger('networkConditionChanged', {
            reason: 'networkConditionChanged',
            stats,
          });
        });
      }
    });

    // The audio stats watcher is only supported on chromium-based browsers that supports
    // the standard version of the getStats API.
    const _supportsAudioStatsWatcher = (env.name === 'Chrome' && env.version >= 58) ||
    (env.name === 'Samsung' && env.version >= 7) ||
      env.isChromiumEdge || env.isOpera;

    let socket = _session._.getSocket();

    const amrState = createAmrStateMachine(AMRStateDurations);
    amrState.on('transitionToP2PTimeout', () => {
      socket.subscriberDestroy(_stream.id, this.widgetId, amrStates.P2P);
      // Unblock the transition to P2P, so we are ready to transition to Routed
      _activeSourceStreamId = amrStates.P2P;
      networkConditionChecker.setSourceStreamId(_activeSourceStreamId);
      amrState.completeTransitionTo(_activeSourceStreamId);
    });

    const getAllPeerConnections = () => values(peerConnectionsAsync);
    const getPeerConnectionBySourceStreamId = sourceStreamId => peerConnectionsAsync[sourceStreamId];
    const getCurrentPeerConnection = () => peerConnectionsAsync[_activeSourceStreamId];
    const removePeerConnectionBySourceStreamId = async (sourceStreamId) => {
      const peerConnection = await peerConnectionsAsync[sourceStreamId];
      await _subscriberStatsHelper.removePeerConnection(peerConnection);
      peerConnection?.close();
      delete peerConnectionsAsync[sourceStreamId];
    };

    const startMigrationTimeout = () => {
      _migrationTimeoutId = setTimeout(() => {
        this._destroy({ reason: 'Migration failed' });
        logAnalyticsEvent('SessionMigration', 'Failed');
      }, _session.MIGRATION_TIMEOUT);
    };

    const removeWebRTCStream = (sourceStreamId) => {
      delete webRTCStreams[sourceStreamId];
    };

    const removeAllPeerConnections = () => {
      // @todo check peer connection destroy triggers disconnect, and then gets logged...
      getAllPeerConnections().forEach(async (peerConnection) => { (await peerConnection).destroy(); });
      Object.keys(peerConnectionsAsync).forEach((key) => { delete peerConnectionsAsync[key]; });
    };

    const activateDownlinkStatsChannel = () => {
      if (shouldSubscribeToDownlinkStatsChannel) {
        shouldSubscribeToDownlinkStatsChannel = false;
        isSubscribedToDownlinkStats = true;
        logAnalyticsEvent('subscribeToSenderStats', 'Attempt', { subscribeToSenderStats: true });
        setChannelActiveStateForSubscriber({
          id: 'downlinkStats1',
          activeState: true,
        });
        logAnalyticsEvent('subscribeToSenderStats', 'Success', { subscribeToSenderStats: true });
      }
    };

    // Note: `isSubscriber` is necessary to prevent changes to the publisher Stream object during self-subscribe
    // See VIDCS-1474 for more context
    const setChannelActiveStateForSubscriber = (attributes) => {
      _stream.setChannelActiveState({
        ...attributes,
        isSubscriber: true,
      });
    };
    const syncUpMediaStreams = (targetStream, originalStream) => {
      updateTrackOnSingleStream(targetStream, targetStream.getAudioTracks()[0],
        originalStream.getAudioTracks()[0]);

      updateTrackOnSingleStream(targetStream, targetStream.getVideoTracks()[0],
        originalStream.getVideoTracks()[0]);
    };
    const bindWebRTCStream = async (webRTCStream) => {
      if (!webRTCStream) {
        return;
      }

      // Sync the track state of the exposed stream with our current stream
      if (_exposedWebRTCStream) {
        syncUpMediaStreams(_exposedWebRTCStream, webRTCStream);
      }

      const videoContainerOptions = {
        error: onVideoError,
        audioVolume: _audioVolume,
        hasVideo: _stream?.hasVideo,
        isSubscribingToVideo: _properties.subscribeToVideo,
      };

      // We want to catch any error in the prebuffering when transition to P2P.
      // Otherwise, we ignore it.
      const throwIfBufferFails = _isAdaptiveEnabled && _activeSourceStreamId === 'P2P';

      // To minimize the blip effect during a stream transition, we take a snapshot of the video stream (if available)
      // and display it instead of a blank screen.
      _widgetView.prepareForStreamTransition();

      try {
        // We hide the loading spinner after we bind to avoid flickering when initially loading the subscriber video.
        // See VIDCS-3672 for more details.
        await _widgetView.bindVideo(webRTCStream, videoContainerOptions, throwIfBufferFails);
        _widgetView.loading(false);
      } catch (err) {
        if (err instanceof CancellationError || _state.isDestroyed()) {
          // don't do anything if the bindVideo was cancelled. In the case of a
          // race condition in which the publisher is destroyed just before the video is being
          // binded, it's not necessary to call onVideoError either, so we can just return here.
          return;
        }
        onVideoError(err);
        throw err;
      }
    };

    const processAudioVideoStats = memoizeLast((stats) => {
      const otStats = {
        timestamp: 0,
      };
      stats.forEach((stat) => {
        if (statsHelpers.isInboundStat(stat)) {
          if (statsHelpers.isVideoStat(stat, stats)) {
            // it is safe to override the timestamp of one by another
            // if they are from the same getStats "batch" video and audio ts have the same value
            otStats.timestamp = statsHelpers.normalizeTimestamp(stat.timestamp);
            merge(otStats, { video: statsHelpers.parseStatCategory(stat, true, stats) });
          } else if (statsHelpers.isAudioStat(stat, stats)) {
            // it is safe to override the timestamp of one by another
            // if they are from the same getStats "batch" video and audio ts have the same value
            otStats.timestamp = statsHelpers.normalizeTimestamp(stat.timestamp);
            merge(otStats, { audio: statsHelpers.parseStatCategory(stat, false) });
          }
        }
      });
      return otStats;
    });

    const processStats = (stats) => {
      const completeStats = {
        ...processAudioVideoStats(stats),
        senderStats: _senderStats,
        mediaLink: _networkConditionStats,
      };
      setDeprecatedProperty(completeStats, 'senderStats', {
        value: _senderStats,
        name: 'senderStats',
        getWarning: 'This property is deprecated and will be removed in the future. Please use mediaLink.transport and mediaLink.remotePublisherTransport properties instead.',
      });
      return completeStats;
    };

    socket.on('signalingNetworkQualityScoreChanged', networkConditionChecker.checkNetworkConditionChange);

    let congestionLevelUpdateCompletionId;
    let congestionLevelUpdateCompletionCallback;
    const completeCongestionLevelUpdate = () => {
      if (!congestionLevelUpdateCompletionId) {
        return false;
      }
      clearTimeout(congestionLevelUpdateCompletionId);
      congestionLevelUpdateCompletionId = null;
      const cb = congestionLevelUpdateCompletionCallback;
      congestionLevelUpdateCompletionCallback = null;
      cb?.();
      return true;
    };

    const updateCongestionLevel = (newCongestionLevel) => {
      if (newCongestionLevel === _congestionLevel) {
        return;
      }
      const prevCongestionLevel = _congestionLevel;
      _congestionLevel = newCongestionLevel;
      // This debounce is needed so there is time for the videoChannel to be deactivated from the publisher,
      // this is how we know PAF is the source.
      // Also to prevent sending duplicated videoEnabled/videoDisabled event.
      // If PAF is not enabled we don't need to debounce.
      if (_stream.publisherAudioFallbackEnabled) {
        if (congestionLevelUpdateCompletionId) {
          clearTimeout(congestionLevelUpdateCompletionId);
        }
        if (_congestionLevel === 2) {
          if (_activeSourceStreamId === 'P2P') {
            // If we are on P2P, we don't need to debounce the check network condition, because in this case
            // it is always from the publisher
            congestionLevelUpdateCompletionCallback = null;
            networkConditionChecker.setCongestionLevel(_congestionLevel);
          } else {
            congestionLevelUpdateCompletionCallback = () => networkConditionChecker.setCongestionLevel(_congestionLevel);
          }
          congestionLevelUpdateCompletionId = setTimeout(completeCongestionLevelUpdate, 100);
          return;
        }
        if (prevCongestionLevel === 2) {
          congestionLevelUpdateCompletionCallback = null;
          congestionLevelUpdateCompletionId = setTimeout(completeCongestionLevelUpdate, 100);
        }
      }
      networkConditionChecker.setCongestionLevel(_congestionLevel);
    };

    eventing(this);

    const _subscriberStatsHelper = createActorStatsHelper({ processStats });

    const _getStatsWrapper = async (source, reportType, callback) => {
      const isRtcStatsReport = reportType === 'rtcStatsReport';
      const isPlainStats = reportType === 'plainStats';

      if (isRtcStatsReport && !isGetRtcStatsReportSupported) {
        const errorCode = ExceptionCodes.GET_RTC_STATS_REPORT_NOT_SUPPORTED;
        callback(otError(
          Errors.GET_RTC_STATS_REPORT_NOT_SUPPORTED,
          new Error(OTErrorClass.getTitleByCode(errorCode)),
          errorCode
        ));
        return;
      }

      if (!isRtcStatsReport && !isPlainStats) {
        const allPCPromises = getAllPeerConnections();
        if (allPCPromises.length === 0) {
          const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
          callback(otError(
            errors.PEER_CONNECTION_NOT_CONNECTED,
            new Error(OTErrorClass.getTitleByCode(errorCode)),
            errorCode
          ));
          return;
        }
        const allPCs = await Promise.all(allPCPromises);
        if (_previousPeerConnection && !allPCs.includes(_previousPeerConnection)) {
          allPCs.push(_previousPeerConnection);
        }
        try {
          const stats = await _subscriberStatsHelper.getStats(allPCs.map(pc => ({ pc })), _activeSourceStreamId);
          callback(null, stats);
        } catch (e) {
          callback(e);
        }
        return;
      }

      const pcPromise = getCurrentPeerConnection();
      if (!pcPromise) {
        const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
        callback(otError(
          errors.PEER_CONNECTION_NOT_CONNECTED,
          new Error(OTErrorClass.getTitleByCode(errorCode)),
          errorCode
        ));
        return;
      }

      const pc = await pcPromise;
      const getStats = isRtcStatsReport ? pc.getRtcStatsReport : pc.getStats;
      getStats(source, (error, stats) => {
        if (error) {
          if (error.code === ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED) {
            error = otError(
              errors.PEER_CONNECTION_NOT_CONNECTED,
              error,
              ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED
            );
          }
          callback(error);
        } else {
          callback(null, stats);
        }
      });
    };

    const _getStats = (source, callback) => _getStatsWrapper(source, null, callback);

    const _getPlainStats = (source, callback) => _getStatsWrapper(source, 'plainStats', callback);

    const _getRtcStatsReport = (source, callback) => _getStatsWrapper(source, 'rtcStatsReport', callback);

    const _streamHasTracks = (kind) => {
      if (!_webRTCStream) {
        return false;
      }
      if (kind === 'video') {
        return _webRTCStream.getVideoTracks().length > 0;
      }

      return _webRTCStream.getAudioTracks().length > 0;
    };

    const logAnalyticsEvent = (action, variation, payload, options, throttle) => {
      let stats = assign({
        action,
        variation,
        payload,
        streamId: _stream ? _stream.id : null,
        sessionId: _session ? _session.sessionId : null,
        connectionId: _session?.isConnected() ? _session?.connection?.connectionId : null,
        partnerId: _session?.sessionInfo?.partnerId ?? null,
        subscriberId: _widgetId,
        widgetType: 'Subscriber',
        singlePeerConnection: !!_session?._singlePeerConnectionController,
      }, options);

      if (variation === 'Failure' ||
        (variation === 'iceconnectionstatechange' && payload === 'closed')) {
        stats = assign(stats, _preDisconnectStats);
      }

      const args = [stats];
      if (throttle) { args.push(throttle); }
      analytics.logEvent(...args);
    };

    const logHybridSessionTransition = (action, variation, payload) => {
      if (variation === 'Attempt') {
        hybridSessionTransitionStartTimes[action] = Date.now();
        logAnalyticsEvent(action, variation, payload);
      } else if (variation === 'Failure' || variation === 'Success') {
        logAnalyticsEvent(action, variation, payload,
          { attemptDuration: Date.now() - hybridSessionTransitionStartTimes[action] });
      }
    };

    const logRoutedToRelayedTransition = (variation, payload = {}) => {
      logHybridSessionTransition('RoutedToRelayedTransition', variation, payload);
    };

    const logRelayedToRoutedTransition = (variation, payload = {}) => {
      logHybridSessionTransition('RelayedToRoutedTransition', variation, payload);
    };

    const _videoQualityWatcher = createVideoQualityWatcher({
      videoQualityChangedChecker: createSubscriberVideoQualityChangedChecker({
        getPcStats: callback => _getStats(STATS_SOURCES.SUBSCRIBER_VIDEO_WATCHER, callback),
        logAnalyticsEvent,
      }),
    });
    _videoQualityWatcher.on('videoQualityChanged', ev => this.trigger('videoQualityChanged', ev));

    let videoQualityChangedListenersCount = 0;
    this.on('videoQualityChanged:added', () => {
      videoQualityChangedListenersCount += 1;
      if (_webRTCStream && videoQualityChangedListenersCount === 1) {
        _videoQualityWatcher.changeInterval(VIDEO_QUALITY_WATCHER_INTERVAL_FAST);
        _videoQualityWatcher.start();
      }
    });
    this.on('videoQualityChanged:removed', () => {
      videoQualityChangedListenersCount = Math.max(videoQualityChangedListenersCount - 1, 0);
      if (videoQualityChangedListenersCount === 0) {
        _videoQualityWatcher.changeInterval(VIDEO_QUALITY_WATCHER_INTERVAL_SLOW);
      }
    });

    this.on('networkConditionChanged:added', activateDownlinkStatsChannel);

    const connectivityState = createConnectivityState({
      onInvalidTransition(transition, from) {
        const err = `Invalid state transition: Event '${transition}' not possible in state '${from}'`;
        logging.error(err);
        logAnalyticsEvent('Subscriber:InvalidStateTransition', 'Event', {
          details: err,
        });
      },
    });

    {
      const errMessage = 'Unable to subscribe to stream in a reasonable amount of time';
      // make sure we trigger an error if we are not getting any "data" after a reasonable
      // amount of time

      overallTimeout({
        connectivityState,
        onWarning() {
          logConnectivityEvent('Warning', {});
        },
        onTimeout: () => {
          if (_widgetView) {
            _widgetView.addError(errMessage);
          }

          if (_state.isAttemptingToSubscribe()) {
            _state.set('Failed');
            this._disconnect({ noStateTransition: true });
            const error = otError(Errors.TIMEOUT, new Error(errMessage), ExceptionCodes.UNABLE_TO_SUBSCRIBE);
            connectivityState.fail({
              options: {
                failureReason: 'Subscribe',
                failureMessage: errMessage,
                failureCode: ExceptionCodes.UNABLE_TO_SUBSCRIBE,
              },
              error,
            });
          }
        },
        warningMs: BIND_VIDEO_DELAY_WARNING,
        timeoutMs: BIND_VIDEO_DELAY_MAX,
      });
    }

    _pcConnected.promise = new Promise((resolve, reject) => {
      _pcConnected.resolve = resolve;
      _pcConnected.reject = reject;
    });

    if (_properties.testNetwork && _session.sessionInfo.p2pEnabled) {
      logging.warn('You cannot test your network with a relayed session. Use a routed session.');
    }

    const _isAutoPreferredResolution = _properties.preferredResolution === 'auto'
      && !_session.sessionInfo.p2pEnabled;

    this.id = _domId;
    this.widgetId = _widgetId;
    this.session = _session;
    this.stream = _properties.stream;
    _stream = _properties.stream;
    this.streamId = _stream.id;

    if (!_session) {
      OTErrorClass.handleJsException({
        errorMsg: 'OT.Subscriber must be passed a session option',
        code: 2000,
        target: this,
        analytics,
      });

      return null;
    }

    const qosQualityScores = {
      audioQualityScore: undefined,
      videoQualityScore: undefined,
    };

    const scoreEmitter = createMOSScoreHelper();
    scoreEmitter.startMonitoringInterval({
      getStats: _getPlainStats,
      subscriber: this,
      getCurrentPeerConnection,
      logging,
      qosQualityScores,
      ExceptionCodes,
    });

    _subscriber.once('subscribeComplete', (...args) => {
      try {
        completionHandler(...args);
      } catch (err) {
        logging.error('Completion handler threw an exception', err);
      }
    });

    const onCaptionReceived = (event) => {
      if (event.streamId === _subscriber.streamId) {
        this.dispatchEvent(event);
        this.trigger(eventNames.SUBSCRIBER_CAPTION_RECEIVED, event);
      }
    };

    _session.on('captionReceived', onCaptionReceived);

    const getVariationFromState = ({ transition, from }) => {
      if (transition === 'fail') {
        if (from === 'connected') {
          return 'Disconnect';
        }
        return 'Failure';
      }
      if (transition === 'disconnect' && from === 'connected') {
        return 'Disconnect';
      }
      return 'Cancel';
    };

    connectivityState.observe({
      onEnterConnecting() {
        logConnectivityEvent('Attempt', null, {});
      },
      onEnterConnected() {
        logConnectivityEvent('Success', null, {});
      },
      onEnterDisconnected(state, { options = {}, payload = null } = {}) {
        logConnectivityEvent(
          getVariationFromState(state),
          payload,
          options
        );
      },
    });

    connectivityState.observe({
      onEnterConnected: () => {
        this.trigger('subscribeComplete', undefined, this);
      },
      onEnterDisconnected: ({ from }, { error } = {}) => {
        if (from === 'connecting') {
          this.trigger('subscribeComplete', error || new Error('An unknown error occurred'), this);
        }
      },
    });

    createConnectivityAttemptPinger({
      connectivityState,
      logAttempt() {
        logAnalyticsEvent('Subscribe', 'Attempting', null, {
          connectionId: _session?.isConnected() ?
            _session?.connection?.connectionId : null,
        });
      },
    });

    const logConnectivityEvent = (variation, payload = {}, options) => {
      if (variation === 'Failure' || variation === 'Success' || variation === 'Cancel') {
        logAnalyticsEvent('Subscribe', variation, payload, {
          ...options,
          attemptDuration: Date.now() - _attemptStartTime,
        });
      } else if (variation === 'Attempt') {
        _attemptStartTime = Date.now();
        logAnalyticsEvent(
          'Subscribe',
          variation,
          { ...payload, testNetwork: _properties.testNetwork },
          options
        );
      } else {
        logAnalyticsEvent('Subscribe', variation, payload, options);
      }
    };

    const logResubscribe = (variation, payload, options) => {
      logAnalyticsEvent('ICERestart', variation, payload, options);
    };

    const recordQOS = ({ parsedStats, remoteConnectionId, peerId, sourceStreamId }) => {
      const QoSBlob = {
        widgetType: 'Subscriber',
        width: _widgetView.width,
        height: _widgetView.height,
        audioTrack: _streamHasTracks('audio'),
        hasAudio: _stream && _stream.hasAudio,
        subscribeToAudio: _isSubscribingToAudio,
        audioVolume: this.getAudioVolume(),
        videoTrack: _streamHasTracks('video'),
        connectionId: _session?.connection?.connectionId ?? null,
        hasVideo: _stream && _stream.hasVideo,
        subscribeToVideo: _properties.subscribeToVideo,
        subscribeToSenderStats: isSubscribedToDownlinkStats,
        congestionLevel: _congestionLevel,
        streamId: _stream.id,
        subscriberId: _widgetId,
        duration: Math.round((now() - _subscribeStartTime) / 1000),
        remoteConnectionId,
        peerId,
        sourceStreamId: getMediaModeBySourceStreamId(sourceStreamId),
        preferredResolutionHeight: _properties.preferredResolution?.height,
        preferredResolutionWidth: _properties.preferredResolution?.width,
        preferredFrameRate: _properties.preferredFrameRate,
        audioFallback: !_videoEnabled && _lastSubscribeToVideoReason === 'auto',
        initials: _stream.initials,
        cpuPerformanceState: _session._.getCpuPerformanceState(),
        localNetworkCondition: _networkConditionStats.transport.networkCondition,
        remoteNetworkCondition: _networkConditionStats.remotePublisherTransport.networkCondition,
        networkDegradationSource: _networkConditionStats.networkDegradationSource,
        signalingNetworkQualityScore: socket?.getSignalingNetworkQualityScore(),
        ...qosQualityScores,
        ..._senderQualityRestrictions,
        ...getNetworkStatus(),
      };
      const combinedStats = assign(QoSBlob, parsedStats);
      analytics.logQOS(combinedStats);
      this.trigger('qos', { ...analytics.commonQoSFields(), ...combinedStats });
    };

    const stateChangeFailed = (changeFailed) => {
      logging.error('OT.Subscriber State Change Failed: ', changeFailed.message);
      logging.debug(changeFailed);
    };

    const onLoaded = () => {
      if (_state.isSubscribing() || !_widgetView || !_widgetView.video()) {
        return;
      }

      _loaded = true;

      if (!_hasLoadedAtLeastOnce) {
        connectivityState.connect();
        _hasLoadedAtLeastOnce = true;
      }

      logging.debug('OT.Subscriber.onLoaded');

      setStateToSubscribing();
      _subscribeStartTime = now();

      if (_chrome) {
        _chrome.showAfterLoading();
      }

      if (_frameRateRestricted) {
        _stream.setRestrictFrameRate(true);
      }

      if (_audioLevelMeter) {
        _audioLevelMeter.audioOnly(_widgetView.audioOnly());
      }

      setAudioVolume(_audioVolume);
      this.trigger('loaded', this);
    };

    const isAudioOn = () =>
      _stream && _stream.hasAudio && _isSubscribingToAudio && this.getAudioVolume() > 0 &&
      !this.isAudioBlocked();

    const isVideoOn = () =>
      _stream && _stream.hasVideo;

    const onAudioLevelStuckWarning = (reason) => {
      // `audioLevelStuckAt0` warning will be ignored either because the video is ON or audio is OFF
      const shouldIgnoreWarning = reason === 'audioLevelStuckAt0' && (!isAudioOn() || isVideoOn());
      let disableAudioLevelStuckAt0 = false;

      if (!shouldIgnoreWarning) {
        logAnalyticsEvent('subscriber:onAudioWarning', 'Event', { reason });
        if (_widgetView) {
          _widgetView.rebindSrcObject();
          logAnalyticsEvent('subscriber:rebindSrcObject', 'Called');

          if (reason === 'audioLevelStuck') {
            // Since 'audioLevelStuck' anomally was detected and the rebind already happened,
            // we don't need to do future rebinds. We can do a return here so that the watcher
            // is not restarted.
            return;
          }

          // This is to ignore future warnings of this type in order to prevent
          // periodical rebindings.
          disableAudioLevelStuckAt0 = true;
        }
      }
      startAudioStatsWatcher(disableAudioLevelStuckAt0);
    };

    const startAudioStatsWatcher = async (disableAudioLevelStuckAt0 = false) => {
      if (_supportsAudioStatsWatcher) {
        const peerConnection = await getCurrentPeerConnection();
        peerConnection.startAudioStatsWatcher(disableAudioLevelStuckAt0);
      }
    };

    const onPeerConnected = (peerConnection) => {
      const isAdaptiveP2pSourceStreamId = _isAdaptiveEnabled && peerConnection.getSourceStreamId() === 'P2P';

      if (isAdaptiveP2pSourceStreamId) {
        logRoutedToRelayedTransition('Success');
      }
    };

    const onPeerDisconnected = () => {
      logging.debug('OT.Subscriber has been disconnected from the Publisher\'s PeerConnection');

      connectivityState.fail({
        options: {
          failureReason: 'PeerConnectionError',
          failureCode: ExceptionCodes.UNABLE_TO_PUBLISH,
          failureMessage: 'PeerConnection disconnected',
        },
        error: otError(
          errors.DISCONNECTED,
          new Error('ClientDisconnected')
        ),
      });

      this._disconnect({ noStateTransition: true });

      if (_state.isAttemptingToSubscribe() || _state.isSubscribing()) {
        _state.set('Failed');
      }
    };

    const onVideoError = (plainErr) => {
      const err = otError(errors.MEDIA_ERR_DECODE, plainErr, plainErr.code || ExceptionCodes.P2P_CONNECTION_FAILED);
      err.message = `OT.Subscriber while playing stream: ${err.message}`;

      logging.error('OT.Subscriber.onVideoError');
      connectivityState.fail({
        options: {
          failureReason: 'VideoElement',
          failureMessage: err.message,
          failureCode: err.code || ExceptionCodes.P2P_CONNECTION_FAILED,
        },
        error: err,
      });

      const isAttemptingToSubscribe = _state.isAttemptingToSubscribe();

      _state.set('Failed');

      if (!isAttemptingToSubscribe) {
        // FIXME: This emits a string instead of an error here for backwards compatibility despite
        // being undocumented. When possible we should remove access to this and other undocumented
        // events, and restore emitting actual errors here.
        _subscriber.trigger('error', err.message);
      }

      OTErrorClass.handleJsException({
        error: err,
        code: ExceptionCodes.UNABLE_TO_SUBSCRIBE, // @todo why do we override the code?
        target: _subscriber,
        analytics,
      });
    };

    const onSubscriberCreateError = (rawError) => {
      // @todo v3 These errors used to come from the peer connection hence the mention
      // of peer connection in the logs/ errors. However they actually have nothing to do
      // with the peer connection. I have chosen to keep the messages / errors the same for
      // now, but we should consider changing them
      const err = interpretSubscriberCreateError(rawError);

      OTErrorClass.handleJsException({
        error: err,
        code: err.code,
        target: _subscriber,
        analytics,
      });

      this._disconnect({ noStateTransition: true });

      const options = {
        failureReason: 'Subscribe',
        failureMessage: `OT.Subscriber PeerConnection Error: ${err.message}`,
        failureCode: ExceptionCodes.P2P_CONNECTION_FAILED,
      };

      _showError(err.code, err.message);

      connectivityState.fail({
        options,
        error: otError(
          err.name,
          new Error('Subscribe: Subscriber PeerConnection with connection (not found) ' +
            `failed: ${err.message}`),
          err.code
        ),
      });

      if (err.name === Errors.STREAM_NOT_FOUND) {
        // Usually, the subscriber should be kept in an error state when the peer connection fails
        // because it still exists in the session. But when we get a 404 that means it doesn't
        // exist, even if the session still thinks it does.
        this._destroy({ reason: 'streamNotFound', noStateTransition: true });
      }
    };

    const onPeerConnectionFailure = (code, reason, peerConnection, prefix) => {
      if (
        prefix === 'SetRemoteDescription' &&
        !_isVideoSupported &&
        reason.match(/Unsupported video without audio for fallback/)
      ) {
        if (_widgetView) {
          _widgetView.addError(
            'The stream is unable to be played.',
            'Your browser does not support the video format.'
          );
        }

        let err;
        if (OTHelpers.env.name === 'Safari') {
          err = new Error(
            'VP8 is not supported in this version of Safari. You might want to consider switching to ' +
            'an H264 project. See https://tokbox.com/developer/sdks/js/safari for more information.'
          );
        } else {
          err = new Error('Video format not supported in this browser.');
        }
        err.code = ExceptionCodes.UNSUPPORTED_VIDEO_CODEC;
        onVideoError(err);
        return;
      }

      if (prefix === 'ICEWorkflow' && _session.sessionInfo.reconnection && _loaded) {
        logging.debug('Ignoring peer connection failure due to possibility of reconnection.');
        return;
      }

      let errorCode;
      if (prefix === 'ICEWorkflow') {
        errorCode = ExceptionCodes.SUBSCRIBER_ICE_WORKFLOW_FAILED;
      } else if (code === ExceptionCodes.STREAM_LIMIT_EXCEEDED) {
        errorCode = code;
      } else {
        errorCode = ExceptionCodes.P2P_CONNECTION_FAILED;
      }
      const options = {
        failureReason: prefix || 'PeerConnectionError',
        failureMessage: `OT.Subscriber PeerConnection Error: ${reason}`,
        failureCode: errorCode,
      };

      const error = interpretPeerConnectionError(
        code,
        reason,
        prefix,
        '(not found)',
        'Subscriber'
      );

      connectivityState.fail({ options, error });

      if (_state.isAttemptingToSubscribe()) {
        // We weren't subscribing yet so this was a failure in setting
        // up the PeerConnection or receiving the initial stream.
        const payload = {
          hasRelayCandidates: peerConnection && peerConnection.hasRelayCandidates(),
        };
        logAnalyticsEvent('createPeerConnection', 'Failure', payload, options);

        _state.set('Failed');
      } else if (_state.isSubscribing()) {
        // we were disconnected after we were already subscribing
        _state.set('Failed');
        this.trigger('error', reason);
      }

      this._disconnect({ noStateTransition: true });

      if (Number(code) === 404) {
        // Usually, the subscriber should be kept in an error state when the peer connection fails
        // because it still exists in the session. But when we get a 404 that means it doesn't
        // exist, even if the session still thinks it does.
        this._destroy({ noStateTransition: true });
      }

      OTErrorClass.handleJsException({
        error,
        errorMsg: `OT.Subscriber PeerConnection Error: ${reason}`,
        code: errorCode,
        target: this,
        analytics,
      });
      _showError(code);
    };

    const hasExpectedTracks = (peerConnection) => {
      const hasVideoTracks = peerConnection._hasVideoTracks();
      const hasAudioTracks = peerConnection._hasAudioTracks();

      // If we're not subbing to video/audio, we don't expect those tracks in the PC
      const shouldHaveVideoTracks = _properties.subscribeToVideo;
      const shouldHaveAudioTracks = _properties.subscribeToAudio;

      const isMissingVideo = (
        _stream && _stream.hasVideo && !hasVideoTracks && shouldHaveVideoTracks
      );
      const isMissingAudio = (
        _stream && _stream.hasAudio && !hasAudioTracks && shouldHaveAudioTracks
      );

      return !(isMissingVideo || isMissingAudio);
    };

    const onTrackAdded = async (webRTCStream, peerConnection) => {
      // Since we get the tracks independently, we will check if we have all the expected
      // tracks already, and when so, we will emit remoteStreamAdded meaning that all expected
      // tracks have arrived.
      try {
        await waitUntil(() => hasExpectedTracks(peerConnection));
      } catch (err) {
        if (err.message === 'TIMEOUT') {
          // If it timeouts, let's log the error but no throw. This will allow us to be more fault
          // tolerant when video fails, e.g. video codecs incompatibility or any other reason.
          logging.error('The expected tracks never arrived');
        } else {
          throw err;
        }
      }

      onRemoteStreamAdded.push({ webRTCStream, peerConnection, sourceStreamId: peerConnection?.getSourceStreamId() });
    };

    const setStream = (webRTCStream, sourceStreamId) => {
      if (!_exposedWebRTCStream) {
        _exposedWebRTCStream = new MediaStream(webRTCStream);
        this.dispatchEvent(new Events.MediaStreamAvailableEvent(_exposedWebRTCStream));
      }
      _webRTCStream = webRTCStream;
      if (sourceStreamId) {
        webRTCStreams[sourceStreamId] = webRTCStream;
      }
    };

    const updateVideoMediaProcessor = (sourceStreamId) => {
      if (_videoEnabled) {
        restoreVideoConnectorStream(sourceStreamId);
      } else {
        _videoMediaProcessorConnector.destroy();
      }
    };

    const setSubscribingOptions = () => {
      // Disable the audio/video, if needed
      this.subscribeToAudio(_isSubscribingToAudio, 'internal');
      if (!(_lastSubscribeToVideoReason === 'auto' && !_properties.subscribeToVideo)) {
        // If subscriber is in audio fallback, don't set reason to 'loading'
        _lastSubscribeToVideoReason = 'loading';
        this.subscribeToVideo(_properties.subscribeToVideo, 'loading');
      }
      this.subscribeToCaptions(_properties.subscribeToCaptions);

      _properties.captionsTranslationLanguage = _properties.captionsTranslationLanguage ?? null;
      if (_properties.captionsTranslationLanguage !== null
        && !validateLanguageCode(_properties.captionsTranslationLanguage)) {
        logging.warn(`Invalid captions translation language: 
        ${_properties.captionsTranslationLanguage}, applying default (null)`);
        _properties.captionsTranslationLanguage = null;
      }
      this.setCaptionsTranslationLanguage(_properties.captionsTranslationLanguage);

      isSubscribedToDownlinkStats = false;
      shouldSubscribeToDownlinkStatsChannel = !!_stream.getChannelsOfType('data').find(channel => channel.id === 'downlinkStats1');
    };

    const setStreamPreferences = ({ p2pEnabled }, sourceStreamId) => {
      // setting resolution and frame rate doesn't work in P2P
      if (!p2pEnabled && sourceStreamId !== 'P2P') {
        if (!_isAutoPreferredResolution) {
          this.setPreferredResolution(_properties.preferredResolution);
        }
        this.setPreferredFrameRate(_properties.preferredFrameRate);
      }
    };

    const setStreamOptions = (sessionInfo, sourceStreamId) => {
      if (_videoMediaProcessorConnector) {
        updateVideoMediaProcessor(sourceStreamId);
      }
      setSubscribingOptions();
      setStreamPreferences(sessionInfo, sourceStreamId);
    };

    const updatePeerConnectionTracks = (peerConnection, forceDisable) => {
      if (peerConnection) { // No peer connection if subscribingToSelf
        peerConnection._getVideoTracks().forEach((track) => {
          track.enabled = !forceDisable && _stream.hasVideo && _properties.subscribeToVideo;
        });
      }
    };

    const handleBindStreamError = (error) => {
      if (isAmrVideoBufferError(error)) {
        logging.error(`OT.Subscriber: ${error.message}`);
        // If videoBuffer failed, we complete the transition to avoid a hanging state.
        onLoaded();
        amrState.completeTransitionTo(_activeSourceStreamId);
        // If it fails when transitioning to P2P, we roll back to Routed. If it fails when
        // transitioning to Routed, then there is nothing we can do.
        if (_activeSourceStreamId === 'P2P') {
          socket.subscriberDestroy(_stream.id, this.widgetId, _activeSourceStreamId);
        }
      }
      throw error;
    };

    const bindStream = async () => {
      logAnalyticsEvent('BindStream', 'Attempt');
      try {
        await bindWebRTCStream(_webRTCStream);
      } catch (error) {
        logAnalyticsEvent('BindStream', 'Failure', { message: error.message });
        handleBindStreamError(error);
      }
      logAnalyticsEvent('BindStream', 'Success');
    };

    const updateVideoProperties = () => {
      const video = _widgetView?.video();
      if (video) {
        video.orientation({
          width: _stream.videoDimensions.width,
          height: _stream.videoDimensions.height,
          videoOrientation: _stream.videoDimensions.orientation,
        });
      }
      _chrome?.videoUnsupportedIndicator?.setVideoUnsupported(!_isVideoSupported);
    };

    const videoElementCreate = () =>
      new Promise((resolve, reject) => {
        const video = _widgetView?.video();
        if (video?.domElement()) {
          resolve();
        } else {
          _widgetView.once('videoElementCreated', resolve);
          _subscriber.once('destroyed', reject);
        }
      });

    const setAudioLevelSampler = (webRTCStream, peerConnection) => {
      // if the audioLevelSampler implementation requires a stream we need to set it now
      if (_audioLevelSampler && 'webRTCStream' in _audioLevelSampler && peerConnection._hasAudioTracks()) {
        _audioLevelSampler.webRTCStream(webRTCStream);
      }
    };

    const startMonitors = (webRTCStream, peerConnection) => {
      setAudioLevelSampler(webRTCStream, peerConnection);
    };

    const handleAdaptiveStreamAdded = async () => {
      amrState.completeTransitionTo(_activeSourceStreamId);
      if (_isSubscribingToAudio && _audioConnector.audioMediaProcessorConnector) {
        // This is only needed for the relayed leg because the connection between the
        // two participants must be created before applying whereas for the routed leg,
        // we can apply the media processor to the track since it is available, but not in use.
        try {
          // We restore the Mantis stream's original track, so when we transition again,
          // we can apply a filter to the unfiltered track.
          await _audioConnector.resetAndApplyAudioConnector(_webRTCStream);
          _logPendingSetAudioMediaProcessorConnector('Success', 'Audio connector applied on routed to relayed transition.');
        } catch (error) {
          if (!_logPendingSetAudioMediaProcessorConnector('Failure', error.message)) {
            logAnalyticsEvent(
              'applyAudioConnectorOnRoutedToRelayedTransition', 'Event', { message: error.message });
          }
        }
      }
    };

    const onRemoteStreamAdded = queue(async ({ webRTCStream, peerConnection }, done) => {
      const sourceStreamId = peerConnection?.getSourceStreamId();
      const { sessionInfo } = _session;

      setStream(webRTCStream, sourceStreamId);

      logging.debug('OT.Subscriber.onRemoteStreamAdded with sourceStreamId', sourceStreamId);
      if (!_state.isMigrating()) {
        _state.set('BindingRemoteStream');
      }

      setStreamOptions(sessionInfo, sourceStreamId);

      // This is a workaround for a bug in Chrome where a track disabled on
      // the remote end doesn't fire loadedmetadata causing the subscriber to timeout
      // https://jira.tokbox.com/browse/OPENTOK-15605
      // Also https://jira.tokbox.com/browse/OPENTOK-16425
      // Also https://jira.vonage.com/browse/OPENTOK-27112
      const forceDisable = !!global.webkitMediaStream;
      updatePeerConnectionTracks(peerConnection, forceDisable);

      try {
        await bindStream();
      } catch (error) {
        // If bindStream throws a buffer AMR error, this is cancelled, and a transition will recover it
        if (!isAmrVideoBufferError(error)) {
          this._destroy(error.reason || 'Stream has failed to bind.');
        }
        return;
      }
      startAudioStatsWatcher();

      if (_state.isDestroyed()) {
        logging.error('Subscriber destroyed');
        return;
      }

      // Enable any video streams that we previously disabled for OPENTOK-27112
      updatePeerConnectionTracks(peerConnection);
      updateVideoProperties();

      // Once videoElement is created and PC connected, the Subscriber is completed.
      await videoElementCreate();
      await _pcConnected.promise;
      if (!_loaded) {
        onLoaded();
      } else if (!_state.isMigrating()) {
        setStateToSubscribing();
      }

      startMonitors(webRTCStream, peerConnection);
      _videoQualityWatcher.changeInterval(videoQualityChangedListenersCount > 0 ? VIDEO_QUALITY_WATCHER_INTERVAL_FAST : VIDEO_QUALITY_WATCHER_INTERVAL_SLOW);
      _videoQualityWatcher.start();

      if (_isAdaptiveEnabled && sourceStreamId === 'P2P') {
        await handleAdaptiveStreamAdded();
      }
      // Async queue will use this callback to run next.
      done();
    });

    const restoreVideoConnectorStream = (sourceStreamId) => {
      if (sourceStreamId === 'P2P' && _originalVideoTrack) {
        // If we are transitioning to P2P, we need to restore the original track in the Mantis stream.
        // Please note we don't add a new stream when transitioning back to Mantis.
        const [filteredVideoTrack] = webRTCStreams.MANTIS.getVideoTracks();
        updateTrackOnSingleStream(webRTCStreams.MANTIS, filteredVideoTrack, _originalVideoTrack);
      }
    };

    const onRemoteVideoSupported = (supported) => {
      // as _isVideoSupported is true by default, this will only ever be hit if we receive
      // a onRemoteVideoSupported false, and then a onRemoteVideoSupported true. In other words
      // it will not trigger an event if video is enabled and is never disabled due to codec issues.

      if (_isVideoSupported !== supported) {
        this.dispatchEvent(new Events.VideoEnabledChangedEvent(
          supported ? 'videoEnabled' : 'videoDisabled',
          {
            reason: supported ? 'codecChanged' : 'codecNotSupported',
          }
        ));
      }
      _isVideoSupported = supported;
    };

    const audioBlockedStateChange = (value) => {
      _widgetView.setAudioBlockedUi(value);

      const indicator = _chrome && _chrome.audioBlockedIndicator;

      if (indicator) {
        indicator.setAudioBlocked(value);
      }

      const videoIndicator = _chrome && _chrome.videoUnsupportedIndicator;

      if (videoIndicator) {
        videoIndicator.setVideoUnsupported(
          !value &&
          !_isVideoSupported
        );
      }
    };

    this.on('audioBlocked', () => { audioBlockedStateChange(true); });
    this.on('audioUnblocked', () => { audioBlockedStateChange(false); });

    const connectionStateMap = {
      new: eventNames.SUBSCRIBER_DISCONNECTED,
      checking: eventNames.SUBSCRIBER_DISCONNECTED,
      connected: eventNames.SUBSCRIBER_CONNECTED,
      completed: eventNames.SUBSCRIBER_CONNECTED,
      disconnected: eventNames.SUBSCRIBER_DISCONNECTED,
      failed: eventNames.SUBSCRIBER_DESTROYED,
    };

    const isConnectionStateChanged = (currentState, lastState) => currentState && currentState !== lastState;

    const destroyAdaptiveRelayedPeerConnection = () => {
      logging.warn(
        'OT.Subscriber: relayed PeerConnection has failed, rolling back to routed PeerConnection'
      );

      socket.subscriberDestroy(_stream.id, this.widgetId, 'P2P');
      logAnalyticsEvent('AMRMantisRollback', 'Event');
      _widgetView.loading(_lastConnectionStatesMap.MANTIS !== 'connected');
    };

    const handleDisconnect = (sourceStreamId) => {
      if (sourceStreamId !== 'P2P') {
        this._.iceRestart('peer connection disconnected');
      }
    };

    const handleFailMantis = () => {
      if (_session._.isSocketConnected()) {
        setTimeout(() => {
          if (_lastConnectionStatesMap.MANTIS === 'destroyed') {
            this._destroy({
              reason: 'peerConnection is disconnected',
              noStateTransition: true,
            });
          }
        // Allow 2s to make sure that a WebSocket reconnection hasn't triggered an iceRestart at
        // the same time the PC has failed.
        }, 2000);
      }
    };

    const handleFailAdaptive = (sourceStreamId) => {
      if (sourceStreamId === 'P2P') {
        // If P2P leg fails in an adaptive session, then we will proceed to destroy it so the
        // transition to Mantis is triggered.
        destroyAdaptiveRelayedPeerConnection();
      } else if (_activeSourceStreamId === 'MANTIS') {
        handleFailMantis();
      }
    };

    const handleFail = (sourceStreamId) => {
      const { p2pEnabled } = _session.sessionInfo;
      if (_isAdaptiveEnabled) {
        handleFailAdaptive(sourceStreamId);
      } else if (!p2pEnabled) {
        handleFailMantis();
      }
    };

    const updateWidgetView = (currentConnectionState) => {
      // We check that the subscriber has loaded so we do not prematurely hide the loading spinner and show a flickering video.
      if (_widgetView && _loaded) {
        _widgetView.loading(currentConnectionState !== eventNames.SUBSCRIBER_CONNECTED);
      }
    };

    const notifyStateChange = (state, currentConnectionState, sourceStreamId) => {
      logging.debug(`OT.Subscriber.connectionStateChanged to ${state}`);

      if (_subscriberConnectionState === currentConnectionState
         || (state === 'failed' && !_session._.isSocketConnected())) {
        return;
      }

      // Allow 2s for a posible iceRestart in Mantis PC happening by a socket reconnection.
      // Please see `handleFailMantis`
      const delayEventMs = state === 'failed' && sourceStreamId === 'MANTIS' ? 2000 : 0;
      setTimeout(() => {
        if (state !== 'failed' || _lastConnectionStatesMap[sourceStreamId] === 'destroyed') {
          this.dispatchEvent(
            new Events.ConnectionStateChangedEvent(
              currentConnectionState,
              this,
              getMediaModeBySourceStreamId(sourceStreamId)
            )
          );
        }
      }, delayEventMs);
    };

    const onIceConnectionStateChange = (state, peerConnection) => {
      const currentConnectionState = connectionStateMap[state];
      const sourceStreamId = peerConnection.getSourceStreamId();
      const lastConnectionState = _lastConnectionStatesMap[sourceStreamId];

      if (!isConnectionStateChanged(currentConnectionState, lastConnectionState)) return;

      _lastConnectionStatesMap[sourceStreamId] = currentConnectionState;

      if (state === 'disconnected') {
        handleDisconnect(sourceStreamId);
      } else if (state === 'failed') {
        handleFail(sourceStreamId);
        // Do nothing else in P2P, since we don't want to destroy the Subscriber or change its
        // video state to loading.
        if (sourceStreamId === 'P2P') return;
      }

      if (sourceStreamId === _activeSourceStreamId) {
        updateWidgetView(currentConnectionState);
        notifyStateChange(state, currentConnectionState, sourceStreamId);
        _subscriberConnectionState = currentConnectionState;
      }
    };

    const onIceRestartAction = (ev, variation, peerConnection, peerId) => {
      const sourceStreamId = peerConnection.getSourceStreamId();
      const payload = {
        isActive: sourceStreamId === _activeSourceStreamId,
      };
      if (variation === 'Attempt' && ev?.reason) {
        payload.reason = ev.reason;
      }
      logResubscribe(variation, payload, { peerId, sourceStreamId });
    };

    const streamUpdated = (event) => {
      const video = _widgetView && _widgetView.video();
      switch (event.changedProperty) {
        case 'videoDimensions':
          if (!video) {
            // Ignore videoDimensions updates before video is created OPENTOK-17253
            break;
          }
          video.orientation({
            width: event.newValue.width,
            height: event.newValue.height,
            videoOrientation: event.newValue.orientation,
          });

          this.dispatchEvent(new Events.VideoDimensionsChangedEvent(
            this, event.oldValue, event.newValue
          ));

          break;

        case 'videoDisableWarning':
          if (_chrome) {
            _chrome.videoDisabledIndicator.setWarning(event.newValue);
          }

          if (event.oldValue !== event.newValue) {
            if (event.newValue) {
              this.dispatchEvent(new Events.VideoDisableWarningEvent(
                'videoDisableWarning'
              ));
              logAnalyticsEvent('videoDisableWarning', 'Event');
              updateCongestionLevel(1);
            } else if (event.oldValue) {
              this.dispatchEvent(new Events.VideoDisableWarningEvent(
                'videoDisableWarningLifted'
              ));
              logAnalyticsEvent('videoDisableWarningLifted', 'Event');
              updateCongestionLevel(null);
            }
          }
          break;

        case 'hasVideo':
          // @todo setAudioOnly affects peer connections, what happens with new ones?
          setAudioOnly(!_stream.hasVideo || !_properties.subscribeToVideo);

          // Makes sure that the audio workaround for Safari is executed.
          if (env.isSafari && !_stream.hasVideo && _widgetView) {
            _widgetView.bindAudioTrackOnly();
          }
          // Prevent event from being triggered due to video being disabled/enabled due to quality
          if (!completeCongestionLevelUpdate()) {
            this.dispatchEvent(new Events.VideoEnabledChangedEvent(
              _stream.hasVideo ? 'videoEnabled' : 'videoDisabled',
              { reason: 'publishVideo' }
            ));
          }
          break;

        case 'hasAudio':
          _muteDisplayMode.update();
          break;

        default:
      }
    };

    // Use _stream.getChannelsOfType instead of _webRTCStream.getAudioTracks
    // because its available as soon as Subscriber is instantiated.
    const _hasAudioTracks = () => _stream.getChannelsOfType('audio').length > 0;

    // / Chrome

    _muteDisplayMode = {
      get() {
        // Use buttonDisplayMode if we have an audio track, even if its muted
        return _hasAudioTracks() ? _subscriber.getStyle('buttonDisplayMode') : 'off';
      },
      update() {
        const mode = _muteDisplayMode.get();
        if (_chrome) {
          _chrome.muteButton.setDisplayMode(mode);
          _chrome.backingBar.setMuteMode(mode);
        }
      },
    };

    const updateChromeForStyleChange = (key, value/* , oldValue */) => {
      if (!_chrome) { return; }

      switch (key) {
        case 'nameDisplayMode':
          _chrome.name.setDisplayMode(value);
          _chrome.backingBar.setNameMode(value);
          break;

        case 'videoUnsupportedDisplayMode':
          _chrome.videoUnsupportedIndicator.setDisplayMode(value);
          break;

        case 'videoDisabledDisplayMode':
          _chrome.videoDisabledIndicator.setDisplayMode(value);
          break;

        case 'audioBlockedDisplayMode':
          _chrome.audioBlockedIndicator.setDisplayMode(value);
          break;

        case 'showArchiveStatus':
          _chrome.archive.setShowArchiveStatus(value);
          break;

        case 'buttonDisplayMode':
          _muteDisplayMode.update();
          break;

        case 'audioLevelDisplayMode':
          _chrome.audioLevel.setDisplayMode(value);
          break;

        case 'bugDisplayMode':
          // bugDisplayMode can't be updated but is used by some partners
          break;

        case 'backgroundImageURI':
          _widgetView.setBackgroundImageURI(value);
          break;

        default:
      }
    };

    const _createChrome = () => {
      const widgets = {
        backingBar: new BackingBar({
          nameMode: !_properties.name ? 'off' : this.getStyle('nameDisplayMode'),
          muteMode: _muteDisplayMode.get(),
        }),

        name: new NamePanel({
          name: _properties.name,
          mode: this.getStyle('nameDisplayMode'),
        }),

        muteButton: new MuteButton({
          muted: _audioVolume === 0,
          mode: _muteDisplayMode.get(),
        }),
      };

      if (_audioLevelCapable) {
        const audioLevelTransformer = new AudioLevelTransformer();

        _audioLevelMeter = new AudioLevelMeter({
          mode: _subscriber.getStyle('audioLevelDisplayMode'),
        });

        const updateAudioLevel = new RafRunner(() => {
          _audioLevelMeter.setValue(audioLevelTransformer.transform(this.loudness));
        });

        _audioLevelMeter.watchVisibilityChanged((visible) => {
          if (visible) {
            updateAudioLevel.start();
          } else {
            updateAudioLevel.stop();
          }
        });

        _audioLevelMeter.setDisplayMode(this.getStyle('audioLevelDisplayMode'));
        _audioLevelMeter.audioOnly(false);

        widgets.audioLevel = _audioLevelMeter;
      }

      widgets.videoDisabledIndicator = new VideoDisabledIndicator({
        mode: this.getStyle('videoDisabledDisplayMode'),
      });

      widgets.audioBlockedIndicator = new AudioBlockedIndicator({
        mode: this.getStyle('audioBlockedDisplayMode'),
      });

      widgets.videoUnsupportedIndicator = new VideoUnsupportedIndicator({
        mode: this.getStyle('videoUnsupportedDisplayMode'),
      });

      if (_widgetView && _widgetView.domElement) {
        _chrome = new Chrome({
          parent: _widgetView.domElement,
        }).set(widgets).on({
          muted() {
            _subscriber.setAudioVolume(0);
          },
          unmuted() {
            _subscriber.setAudioVolume(_latestPositiveVolume);
          },
        }, this);
        // Hide the chrome until we explicitly show it
        _chrome.hideWhileLoading();
      }
    };

    const _showError = (code, message) => {
      let errorMessage;
      let helpMessage;

      // Display the error message inside the container, assuming it's
      // been created by now.
      if (_widgetView) {
        if (code === ExceptionCodes.STREAM_LIMIT_EXCEEDED) {
          errorMessage = 'The stream was unable to connect.';
          helpMessage = 'The limit for the number of media streams has been reached.';
        } else if (message === 'Tried to subscribe to an encrypted stream without enabling encryption') {
          errorMessage = 'Unable to subscribe to an encrypted stream without enabling encryption.';
          helpMessage = '';
        } else {
          errorMessage = 'The stream was unable to connect due to a network error.';
          if (_hasLoadedAtLeastOnce) {
            helpMessage = 'Ensure you have a stable connection and try again.';
          } else {
            helpMessage = 'Make sure you have a stable network connection and that it isn\'t ' +
              'blocked by a firewall.';
          }
        }

        _widgetView.addError(errorMessage, helpMessage);
      }
    };

    StylableComponent(this, {
      nameDisplayMode: 'auto',
      buttonDisplayMode: 'auto',
      audioLevelDisplayMode: 'auto',
      videoDisabledDisplayMode: 'auto',
      audioBlockedDisplayMode: 'auto',
      backgroundImageURI: null,
      showArchiveStatus: true,
      showMicButton: true,
    }, _properties.showControls, (payload) => {
      logAnalyticsEvent('SetStyle', 'Subscriber', payload, null, 0.1);
    });

    function setAudioOnly(audioOnly) {
      const peerConnectionUpdates = getAllPeerConnections().map(async peerConnection =>
        (await peerConnection).subscribeToVideo(!audioOnly)
      );

      // Default to audioFallback if no mediaStream or no video tracks.
      const audioFallback = audioOnly || !_webRTCStream ||
       !_webRTCStream.getVideoTracks().length;

      if (_widgetView) {
        _widgetView.audioOnly(audioFallback);
        _widgetView.showPoster(audioFallback);
      }

      if (_audioLevelMeter) {
        _audioLevelMeter.audioOnly(audioFallback);
      }

      return peerConnectionUpdates;
    }

    // logs an analytics event for getStats on the first call
    const notifyGetStatsCalled = once(() => {
      logAnalyticsEvent('GetStats', 'Called');
    });

    const notifyGetRtcStatsReportCalled = once(() => {
      logAnalyticsEvent('GetRtcStatsReport', 'Called');
    });

    const createAudioLevelSampler = async (peerConnection) => {
      if (_audioLevelBehaviour) {
        _audioLevelBehaviour.destroy();
        _audioLevelBehaviour = undefined;
      }

      try {
        await waitUntil(() => peerConnection._hasAudioTracks());
      } catch (err) {
        // No need to create an audio level sampler, if there's no audio tracks
        return;
      }

      // prefer the audioLevelSampler (more efficient and better responsiveness)
      _audioLevelSampler = audioLevelSamplerFactory(peerConnection);

      if (_audioLevelSampler) {
        _audioLevelBehaviour = audioLevelBehaviour({ subscriber: this, audioLevelSampler: _audioLevelSampler });
      } else if (!Object.prototype.hasOwnProperty.call(this, 'loudness')) {
        Object.defineProperty(
          this,
          'loudness',
          { value: undefined, configurable: true, writable: false }
        );
        logging.error('No suitable audio level samplers found, audio level visualisation will not work');
      }
    };

    const setPeerConnectionEvents = (peerConnection, sourceStreamId, peerId) => {
      if (_peerConnectionEvents[sourceStreamId]) {
        _peerConnectionEvents[sourceStreamId].removeAll();
        const onDisconnected = () => {
          peerConnection.off('error', onDisconnected);
          peerConnection.off('disconnected', onDisconnected);
        };
        peerConnection.on('error', onDisconnected);
        peerConnection.on('disconnected', onDisconnected);
      }

      const peerConnectionEvents = eventHelper(peerConnection);

      peerConnectionEvents.once('connected', () => onPeerConnected(peerConnection));
      peerConnectionEvents.on('disconnected', onPeerDisconnected);
      peerConnectionEvents.on('error', onPeerConnectionFailure);
      peerConnectionEvents.once('remoteTrackAdded', async (...args) => {
        await createAudioLevelSampler(peerConnection);
        try {
          await onTrackAdded(...args);
        } catch (err) {
          logging.error(err);
        }
      });
      peerConnectionEvents.on('audioLevelStuckWarning', onAudioLevelStuckWarning);
      peerConnectionEvents.on('signalingStateStable', async () => {
        _subscriber.trigger('signalingStateStable');
        const video = _widgetView && _widgetView.video();
        if (video && _webRTCStream) {
          if (_audioLevelSampler && 'webRTCStream' in _audioLevelSampler
              && peerConnection._hasAudioTracks()) {
            _audioLevelSampler.webRTCStream(_webRTCStream);
          }
        }
      });

      peerConnectionEvents.on('iceConnectionStateChange', state => onIceConnectionStateChange(state, peerConnection));
      peerConnectionEvents.on('iceRestartAttempt', ev => onIceRestartAction(ev, 'Attempt', peerConnection, peerId));
      peerConnectionEvents.on('iceRestartSuccess', ev => onIceRestartAction(ev, 'Success', peerConnection, peerId));
      peerConnectionEvents.on('iceRestartFailure', ev => onIceRestartAction(ev, 'Failure', peerConnection, peerId));
      peerConnectionEvents.on('remoteVideoSupported', onRemoteVideoSupported);

      peerConnectionEvents.on('decryptFailed', () => {
        this.trigger('encryptionSecretMismatch');
        logAnalyticsEvent('encryptionSecretMismatch', 'Event');
        logging.warn('Media cannot be decrypted due to an encryption secret mismatch.');
      });
      peerConnectionEvents.on('decryptRestored', () => {
        this.trigger('encryptionSecretMatch');
        logAnalyticsEvent('encryptionSecretMatch', 'Event');
      });

      _peerConnectionEvents[sourceStreamId] = peerConnectionEvents;
    };

    const _transitionRoutedToRelayed = async () => {
      logRoutedToRelayedTransition('Attempt');

      const mantisPeerConnection = await getPeerConnectionBySourceStreamId('MANTIS');

      if (!mantisPeerConnection?.iceConnectionStateIsConnected()) {
        // This is for avoiding corner cases, e.g. two subscribers that join concurrently.
        // The first one will connect first to MANTIS and then receive a stream created
        // signal from RUMOR for the new P2P stream, so it will transition to Relayed.
        // The second one - that joined a few ms later - will also receive the stream created signal
        // from RUMOR, but is not connected to MANTIS yet, so this second one shouldn't attempt
        // to transition to Relayed, because 1) this means there's another subscriber that did
        // the transition, and 2) our model is MANTIS first, so we shouldn't transition to
        // Relayed in this case.
        logRoutedToRelayedTransition('Failure', {
          reason: 'Attempted to transition to Relayed without being connected in Routed mode.',
        });
        return;
      }

      socket.subscriberCreate(
        _stream.id,
        _widgetId, // subscriberId
        channelsToSubscribeTo,
        'P2P',
        _session._.isE2ee(),
        (err, message) => {
          if (err && !connectivityState.is('disconnected')) {
            logRoutedToRelayedTransition('Failure', err);
          }
          logging.debug('message received when created a subscriber with source', message);
        }
      );
    };

    const _transitionRelayedToRouted = async () => {
      if (_properties.testNetwork || isLocalStream(_stream, _session)
       || _activeSourceStreamId !== 'P2P') {
        // In the case we are doing a network test, subscribing the local stream or receive
        // a transition message from Rumor in an incomplete Subscriber: we ignore the signal
        // to transition to routed.
        return;
      }

      onRemoteStreamAdded.remove(({ data: { sourceStreamId } }) => sourceStreamId === 'P2P');

      logRelayedToRoutedTransition('Attempt');

      if (_webRTCStream !== webRTCStreams.MANTIS) {
        _webRTCStream = webRTCStreams.MANTIS;

        if (_videoEnabled && _videoMediaProcessorConnector) {
          await _setVideoMediaProcessorConnector();
        }

        if (_audioConnector.audioMediaProcessorConnector && _isSubscribingToAudio) {
          try {
            // we don't restore the P2P stream, because it will be destroyed
            await _audioConnector.setAudioMediaProcessorConnector(_webRTCStream);
            _logPendingSetAudioMediaProcessorConnector(
              'Success', 'Audio connector applied on relayed to routed transition.');
          } catch (error) {
            if (!_logPendingSetAudioMediaProcessorConnector('Failure', error.message)) {
              logAnalyticsEvent(
                'applyAudioConnectorOnRelayedToRoutedTransition', 'Event', { message: error.message });
            }
          }
        }

        // bind mantis stream
        await bindWebRTCStream(_webRTCStream);
      }

      // Update the active source stream ID immediately to prevent any concurrent code
      // (e.g. setAudioMediaProcessorConnector) from applying operations to the outgoing
      // P2P stream after the MANTIS stream has been bound to the video element.
      _activeSourceStreamId = 'MANTIS';
      networkConditionChecker.setSourceStreamId(_activeSourceStreamId);

      // remove and close p2p connection
      await removePeerConnectionBySourceStreamId('P2P');

      // Keep the loading spinner hidden so that the transition looks transparent.
      _widgetView.loading(false);

      // remove webRTCStream
      removeWebRTCStream('P2P');

      // Create audioLevelSampler for the current peer connection
      const mantisPC = await getPeerConnectionBySourceStreamId('MANTIS');
      await createAudioLevelSampler(mantisPC);
      startMonitors(_webRTCStream, mantisPC);

      logRelayedToRoutedTransition('Success');
    };

    this._destroy = ({ reason = 'Unknown', quiet, noStateTransition = false }) => {
      if (_state.isDestroyed()) { return this; }

      _state.set('Destroyed');

      _videoQualityWatcher.destroy();
      amrState.destroy();
      logSetAudioVolumeLimitedRunner.destroy();

      if (_audioLevelBehaviour) {
        _audioLevelBehaviour.destroy();
        _audioLevelBehaviour = undefined;
      }

      _preDisconnectStats = {
        sessionId: _session.sessionId,
        connectionId: _session?.isConnected() ? _session?.connection?.connectionId : null,
        partnerId: _session?.sessionInfo?.partnerId ?? null,
        streamId: (_stream && !_stream.destroyed) ? _stream.id : null,
      };

      this._disconnect({ reason, noStateTransition });

      if (_chrome) {
        _chrome.destroy();
        _chrome = null;
      }

      _session.off('captionReceived', onCaptionReceived);

      if (_widgetView) {
        _widgetView.destroy();
        _widgetView.off();
        _widgetView = null;
        this.element = null;
      }

      networkConditionChecker.destroy();

      if (_stream && !_stream.destroyed) {
        logAnalyticsEvent('unsubscribe', null, { streamId: _stream.id });
      }

      _stream.off(_streamEventHandlers, this);

      this.id = null;
      _domId = null;
      this.stream = null;
      _stream = null;
      this.streamId = null;

      this.session = null;
      _session = null;
      _properties = null;

      if (quiet !== true) {
        this.dispatchEvent(new Events.DestroyedEvent(
          eventNames.SUBSCRIBER_DESTROYED,
          this,
          reason
        ));
        this.off();
      }

      return this;
    };

    this.destroy = (reason = 'Unsubscribe', quiet) => {
      logging.warn('Subscriber#destroy is deprecated and will be removed. Please use Session#unsubscribe instead');
      this._destroy({ reason, quiet });
    };

    this._disconnect = ({ reason = 'Unknown', noStateTransition } = {}) => {
      // known reasons:
      // forceUnpublished (publisher stream was destroyed by forceUnpublish)
      // clientDisconnected (publisher unpublished)
      // Unsubscribe (when calling session.unsubscribe)
      // Unknown (when reason is not determined)

      if (!noStateTransition && !connectivityState.is('disconnected')) {
        const error = reason === 'Unsubscribe' ? undefined : otError(
          errors.STREAM_DESTROYED,
          new Error('Stream was destroyed before it could be subscribed to')
        );

        connectivityState.disconnect({ payload: { reason }, error });
      }

      if (!_state.isDestroyed() && !_state.isFailed()) {
        // If we are already in the destroyed state then disconnect
        // has been called after (or from within) destroy.
        _state.set('NotSubscribing');
      }

      // Clear pending tasks
      clearPending(this);

      if (_widgetView) {
        _widgetView.destroyVideo();
      }

      // Unsubscribe us from the stream, if it hasn't already been destroyed
      if (socket.is('connected') && _stream && !_stream.destroyed) {
        // Notify the server components
        // @todo I assume we don't want to send this message, but is there anything
        // we need to do in p2p->mantis for when a peer conn is destroyed?
        socket.subscriberDestroy(_stream.id, this.widgetId);
      }

      removeAllPeerConnections();
    };

    this.disconnect = () => {
      logging.warn('Subscriber#disconnect is deprecated and will be removed. Please use Session#unsubscribe instead');
      this._disconnect({ reason: 'Unsubscribe' });
    };

    const processOffer = async ({ peerId, fromConnectionId, sourceStreamId }) => {
      const uri = constructSubscriberUri({
        apiKey: _session.apiKey,
        sessionId: _session.sessionId,
        streamId: _stream.id,
        subscriberId: this.widgetId,
      });

      const send = createSendMethod({
        socket: this.session._.getSocket(),
        uri,
        content: {
          peerId,
          sourceStreamId,
        },
      });

      const log = (action, variation, payload, options = {}, throttle) => {
        const transformedOptions = {
          peerId,
          sourceStreamId: getMediaModeBySourceStreamId(sourceStreamId),
          ...options };
        return logAnalyticsEvent(action, variation, payload, transformedOptions, throttle);
      };

      const logQoS = (qos) => {
        // We only log data from the active peer connection
        if (_activeSourceStreamId !== sourceStreamId) {
          return;
        }

        recordQOS({ ...qos, peerId, remoteConnectionId: fromConnectionId, sourceStreamId });
      };

      return this._setupPeerConnection({ send, log, logQoS, sourceStreamId, peerId });
    };

    // When migrating, a P2P transition is performed as a renegotiation, since we already have a PC
    // we don't need to create a new one, just accept the new offer.
    const isRoutedToRelayedTransitionDuringMigration = (type, sourceStreamId) =>
      _isAdaptiveEnabled
      && sourceStreamId === 'P2P'
      && type === 'offer'
      && _state.isMigrating();

    this.processMessage = async (type, fromConnectionId, message) => {
      logging.debug(`OT.Subscriber.processMessage: Received ${type} message from ${fromConnectionId}`);
      logging.debug(message);

      const peerId = get(message, 'content.peerId');
      const sourceStreamId = get(message, 'content.sourceStreamId');

      let peerConnection = await getPeerConnectionBySourceStreamId(sourceStreamId);

      if (type === 'offer'
         && (!peerConnection || (_state.isMigrating() && sourceStreamId === 'MANTIS'))) {
        peerConnection = await processOffer({ peerId, fromConnectionId, sourceStreamId });
        if (_shouldUseSpc && sourceStreamId === 'MANTIS') {
          _session._singlePeerConnectionController.addSubscriber(this.widgetId, () => {
            peerConnection.processMessage('offer', message);
          });
        } else {
          // Pace subscriber creation when MPC.
          this.session._.addSubscriberToPeerConnectionsQueue(peerConnection, message);
        }
      } else if (peerConnection) {
        peerConnection.processMessage(type, message);
        if (isRoutedToRelayedTransitionDuringMigration(type, sourceStreamId)) {
          const relayedStream = {
            webRTCStream: webRTCStreams.P2P,
            peerConnection,
          };
          onRemoteStreamAdded.push(relayedStream);
          _activeSourceStreamId = 'P2P';
          networkConditionChecker.setSourceStreamId(_activeSourceStreamId);
          logRoutedToRelayedTransition('Success');
        }
      }
    };

    this.disableVideo = (active) => {
      if (!active) {
        logging.warn('Due to high packet loss and low bandwidth, video has been disabled');
      } else if (_lastSubscribeToVideoReason === 'auto' && _isVideoManuallyMuted === false) {
        logging.info('Video has been re-enabled');
      } else {
        logging.info('Video was not re-enabled because it was manually disabled');
        if (_chrome && _chrome.videoDisabledIndicator) {
          // We won't enable the video, but clear any disabled video icons we have enabled
          _chrome.videoDisabledIndicator.disableVideo(false);
        }
        return;
      }

      this.subscribeToVideo(active, 'auto');
      const payload = active ? { videoEnabled: true } : { videoDisabled: true };
      logAnalyticsEvent('updateQuality', 'video', payload);
    };

    /**
     * Returns the base-64-encoded string of PNG data representing the Subscriber video.
     *
     *  <p>You can use the string as the value for a data URL scheme passed to the src parameter of
     *  an image file, as in the following:</p>
     *
     *  <pre>
     *  var imgData = subscriber.getImgData();
     *
     *  var img = document.createElement("img");
     *  img.setAttribute("src", "data:image/png;base64," + imgData);
     *  var imgWin = window.open("about:blank", "Screenshot");
     *  imgWin.document.write("&lt;body&gt;&lt;/body&gt;");
     *  imgWin.document.body.appendChild(img);
     *  </pre>
     * @method #getImgData
     * @memberOf Subscriber
     * @return {String} The base-64 encoded string. Returns an empty string if there is no video.
     */
    this.getImgData = () => {
      if (!this.isSubscribing()) {
        logging.error('OT.Subscriber.getImgData: Cannot getImgData before the Subscriber ' +
          'is subscribing.');
        return null;
      }

      const video = _widgetView && _widgetView.video();
      return video ? video.imgData() : null;
    };

    /**
    *  Returns the details on the subscriber stream quality, including the following:
    *
    * <ul>
    *
    *   <li>Total audio and video packets lost</li>
    *   <li>Total audio and video packets received</li>
    *   <li>Total audio and video bytes received</li>
    *   <li>Actual received video resolution (via <a href="#videoWidth">videoWidth()</a> and
    *     <a href="#videoHeight">videoHeight()</a>)</li>
    *   <li>Actual decoded video frame rate (via <code>video.decodedFrameRate</code>)</li>
    *
    * </ul>
    *
    * You can publish a test stream, subscribe to it (on the publishing client), and use this method
    * to check its quality. Based on the stream's quality, you can determine what video resolution is
    * supported and whether conditions support video or audio. You can then publish an appropriate
    * stream, based on the results. When using this method to test a stream published by your
    * own client, set the <code>testNetwork</code> property to <code>true</code> in the options you
    * pass into the <a href="Session.html#subscribe">Session.subscribe()</a> method. For an example,
    * see the <a href="https://github.com/opentok/opentok-network-test">opentok-network-test</a>
    * project on GitHub.
    * <p>
    * You may also use these statistics to have a Subscriber subscribe to audio-only if the audio
    * packet loss reaches a certain threshold. If you choose to do this, you should set the
    * <code>subscriber</code> property of the <code>audioFallback</code> object to <code>false</code>
    * when you initialize Publisher objects for the session. This prevents the
    * Vonage Video Media Router from using its own audio-only toggling implementation.
    * (See the documentation for the <a href="OT.html#initPublisher">OT.initPublisher()</a> method.)
    *
    * @param {Function} completionHandler An optional function that takes the following
    * parameters:
    *
    * <ul>
    *
    *   <li><code>error</code> (<a href="Error.html">Error</a>) &mdash; The error property is
    *   set if the client is not connected. Upon completion of a successful call to the method,
    *   this property is undefined.</li>
    *
    *   <li><code>stats</code> (Object) &mdash; An object with the following properties:
    *     <p>
    *     <ul>
    *       <li><code>audio.bytesReceived</code> (Number) &mdash; The total number of audio bytes
    *         received by the subscriber</li>
    *       <li><code>audio.packetsLost</code> (Number) &mdash; Total audio packets that did not reach
    *         the subscriber</li>
    *       <li><code>audio.packetsReceived</code> (Number) &mdash; The total number of audio packets
    *         received by the subscriber</li>
    *       <li><code>timestamp</code> (Number) &mdash; The timestamp, in milliseconds since the Unix
    *         epoch, for when these stats were gathered</li>
    *       <li><code>video.bytesReceived</code> (Number) &mdash; The total video bytes received by
    *         the subscriber</li>
    *       <li><code>video.packetsLost</code> (Number) &mdash; The total number of video packets that
    *         did not reach the subscriber</li>
    *       <li><code>video.packetsReceived</code> (Number) &mdash; The total number of video packets
    *         received by the subscriber</li>
    *       <li><code>video.frameRate</code> (Number) &mdash; The current average video frame rate. This property is
    *         deprecated and will be removed in the future. Please instead use the <code>video.decodedFrameRate</code> property.</li>
    *       <li><code>video.decodedFrameRate</code> (Number) &mdash; The decoded video frame rate (in frames per second)</li>
    *       <li><code>video.codec</code> (String) &mdash; The video codec used by this subscriber</li>
    *       <li><code>video.pauseCount</code> (Number) &mdash; Number of times the subscriber's video
    *         playback has paused. A pause is detected if no frame is rendered for more than 5 seconds</li>
    *       <li><code>video.totalPausesDuration</code> (Number) &mdash; Cumulative duration (in milliseconds)
    *         of all detected pauses in video playback</li>
    *       <li><code>video.freezeCount</code> (Number) &mdash; Number of times the subscriber’s video has
    *         frozen. A freeze is defined based on the [WebRTC stats spec](https://www.w3.org/TR/webrtc-stats/#dom-rtcinboundrtpstreamstats-freezecount)</li>
    *       <li><code>video.totalFreezesDuration</code> (Number) &mdash; Cumulative duration (in milliseconds)
    *         of all freezes detected in the subscriber’s video playback</li>
    *       <li><code>senderStats.connectionMaxAllocatedBitrate</code> (Number) &mdash; The maximum
    *         bitrate that can be estimated for the sender connection, in bps (bits per second)</li>
    *       <li><code>senderStats.connectionEstimatedBandwidth</code> (Number) &mdash; The current bandwidth
    *         estimation for the sender connection, in bps (bits per second)</li>
    *       <li><code>transport.connectionEstimatedBandwidth</code> (Number) &mdash; The estimated
    *         available bandwidth for the local connection, in bits per second (bps)</li>
    *       <li><code>transport.networkCondition</code> (String) &mdash; Current network condition state
    *         of the local connection. Possible values are: <code>unknown</code>, <code>critical</code>,
    *         <code>warning</code>, <code>fair</code>, <code>good</code> and <code>excellent</code>.</li>
    *       <li><code>transport.networkConditionReason</code> (String) &mdash; Main reason impacting local
    *         network condition. Possible values are: <code>none</code>, <code>unknown</code>, <code>bandwidth</code>
    *         and <code>packetLoss</code>.</li>
    *       <li><code>remotePublisherTransport.connectionEstimatedBandwidth</code> (Number) &mdash; The
    *         estimated available bandwidth for the remote publisher connection, in bits per second (bps)</li>
    *       <li><code>remotePublisherTransport.networkCondition</code> (String) &mdash; Current network
    *         condition state of the remote publisher connection. Possible values are: <code>unknown</code>,
    *         <code>critical</code>, <code>warning</code>, <code>fair</code>, <code>good</code> and
    *         <code>excellent</code>.</li>
    *       <li><code>remotePublisherTransport.networkConditionReason</code> (String) &mdash; Main reason
    *         impacting remote publisher network condition. Possible values are: <code>none</code>, <code>unknown</code>,
    *         <code>bandwidth</code> and <code>packetLoss</code>.</li>
    *       <li><code>networkDegradationSource</code> (String) &mdash; In case of network degradation, indicates
    *         which side caused it. Possible values are: <code>none</code>, <code>bothOrUnclear</code>,
    *         <code>local</code> and <code>remote</code>.</li>
    *     </ul>
    *   </li>
    * </ul>
    *
    * @returns {Promise<SubscriberStats>}
    *
    * @see <a href="Publisher.html#getStats">Publisher.getStats()</a>
    * @see <a href="#getRtcStatsReport">Publisher.getRtcStatsReport()</a>
    *
    * @method #getStats
    * @memberOf Subscriber
    */
    this.getStats = (callback = () => {}) => new Promise((resolve, reject) => {
      activateDownlinkStatsChannel();
      notifyGetStatsCalled();
      _getStats(STATS_SOURCES.SDK, (error, stats) => {
        if (error) {
          callback(error);
          reject(error);
          return;
        }
        callback(null, stats);
        resolve(stats);
      });
    });

    /**
    * Returns a promise that, on success, resolves with an RtcStatsReport object
    * for the subscribed stream. (See
    * <a href="https://developer.mozilla.org/en-US/docs/Web/API/RTCStatsReport" target="_blank">
    * RTCStatsReport</a>.)
    * <p>
    * The Promise will be rejected in the following conditions:
    * <ul>
    *   <li>
    *     The browser does not support this method (for example, in Chrome version 57 and lower,
    *     which does not support the <a href="https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/getStats" target="_blank">
    *     RTCPeerConnection.getStats()</a> standard).
    *   </li>
    *   <li>
    *     The <a href="https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection" target="_blank">
    *     PeerConnection</a> for the Subscriber is not connected.
    *   </li>
    *  </ul>
    * </p>
    *
    * @method #getRtcStatsReport
    * @memberOf Subscriber
    *
    * @see <a href="Publisher.html#getRtcStatsReport">Publisher.getRtcStatsReport()</a>
    *
    * @return {Promise} A promise that resolves when the operation completes successfully.
    * If there is an error, the promise is rejected.
    */
    this.getRtcStatsReport = () => {
      notifyGetRtcStatsReportCalled();
      return new Promise((resolve, reject) => {
        _getRtcStatsReport(STATS_SOURCES.SDK_RAW, (error, rtcStatsReport) => {
          if (error) {
            reject(error);
          } else {
            resolve(rtcStatsReport);
          }
        });
      });
    };

    function applyAudioVolume(audioVolume) {
      const video = _widgetView && _widgetView.video();
      const promises = [];
      if (video) {
        try {
          video.setAudioVolume(audioVolume);
        } catch (e) {
          logging.warn(`setAudioVolume: ${e}`);
          if (_audioVolume === 0) {
            logging.info('Using subscribeToAudio to mute Audio because setAudioVolume(0) failed');
            // If we can't set the audioVolume to 0 then at least mute by setting subscribeToAudio(false)
            promises.push(_subscriber.subscribeToAudio.promise(false, 'internal'));
          }
        }
      }

      if (_chrome) {
        _chrome.muteButton.muted(audioVolume === 0);
      }

      return Promise.all(promises);
    }

    function setAudioVolume(requestedVolume, callback = () => {}) {
      const volume = normalizeAudioVolume(requestedVolume);
      const rangeErrorMessage = 'value should be an integer between 0 and 100';
      if (isNaN(volume)) {
        logging.error(`OT.Subscriber.setAudioVolume: ${rangeErrorMessage}`);
        callback(new Error(rangeErrorMessage));
        return { volume, error: rangeErrorMessage };
      }

      if (volume !== requestedVolume) {
        logging.warn(`OT.Subscriber.setAudioVolume: ${rangeErrorMessage}`);
      }

      if (volume === _audioVolume) {
        applyAudioVolume(_audioVolume)
          .then(() => callback())
          .catch(err => callback(err));
        return { volume, message: 'Requested volume is same as already set audioVolume' };
      }

      if (_audioVolume > 0) {
        _latestPositiveVolume = _audioVolume;
      }

      _audioVolume = volume;

      const promises = [applyAudioVolume(_audioVolume)];

      if (_audioVolume > 0 && !_isSubscribingToAudio) {
        // in Firefox (and others) we don't stop subscribing to audio when muted, however if we are 'unmuting' and in
        // the subscribeToAudio: false state we should subscribe to audio again

        // subscribeToAudio is going to call us with _latestPositiveVolume so we'll update it here
        _latestPositiveVolume = _audioVolume;
        promises.push(_subscriber.subscribeToAudio.promise(true, 'internal'));
      }

      Promise.all(promises)
        .then(() => callback())
        .catch(err => callback(err));

      return { volume };
    }

    const logSetAudioVolumeLimitedRunner = createRateLimitedRunner((result) => {
      const { volume, error, message } = result;
      logAnalyticsEvent('setAudioVolume', 'Attempt', { audioVolume: volume });
      if (error) {
        logAnalyticsEvent('setAudioVolume', 'Failure', { message: error });
      } else {
        logAnalyticsEvent('setAudioVolume', 'Success', { audioVolume: volume, message });
      }
    });

    const setAudioVolumeWithCallback = (requestedVolume, callback = () => {}) => {
      const result = setAudioVolume(requestedVolume, callback);
      logSetAudioVolumeLimitedRunner.run(result);
    };

    /**
     * Sets the audio volume, between 0 and 100, of the Subscriber.
     *
     * <p>You can set the initial volume when you call the <code>Session.subscribe()</code>
     * method. Pass a <code>audioVolume</code> property of the <code>properties</code> parameter
     * of the method.</p>
     *
     * @param {Number} value The audio volume, between 0 and 100.
     *
     * @return {Subscriber} The Subscriber object. This lets you chain method calls, as in the
     * following:
     *
     * <pre>mySubscriber.setAudioVolume(50).setStyle(newStyle);</pre>
     *
     * @see <a href="#getAudioVolume">getAudioVolume()</a>
     * @see <a href="Session.html#subscribe">Session.subscribe()</a>
     * @method #setAudioVolume
     * @memberOf Subscriber
     */
    this.setAudioVolume = (requestedVolume) => {
      setAudioVolumeWithCallback(requestedVolume);
      return this;
    };

    /**
     * Promise-based variant of {@link Subscriber#setAudioVolume}.
     *
     * Sets the audio volume of the Subscriber and returns a Promise that settles once all
     * underlying audio operations have completed (including any audio-processor connector
     * update triggered by unmuting).
     *
     * @param {Number} value - The audio volume, between 0 and 100.
     * @returns {Promise<void>} Resolves when the volume has been successfully applied;
     *   rejects with an <code>Error</code> if the value is out of range or if any
     *   underlying audio operation fails.
     *
     * @example
     * subscriber.setAudioVolume.promise(50)
     *   .then(() => console.log('volume set'))
     *   .catch(err => console.error('failed to set volume', err));
     *
     * @method #setAudioVolume.promise
     * @memberOf Subscriber
     */
    this.setAudioVolume.promise = requestedVolume => new Promise((resolve, reject) => {
      setAudioVolumeWithCallback(requestedVolume, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    /**
    * Returns the audio volume, between 0 and 100, of the Subscriber.
    *
    * <p>Generally you use this method in conjunction with the <code>setAudioVolume()</code>
    * method.</p>
    *
    * @return {Number} The audio volume, between 0 and 100, of the Subscriber.
    * @see <a href="#setAudioVolume">setAudioVolume()</a>
    * @method #getAudioVolume
    * @memberOf Subscriber
    */
    this.getAudioVolume = () => {
      const video = _widgetView && _widgetView.video();
      if (video) {
        try {
          return video.getAudioVolume();
        } catch (e) {
          logging.warn(`getAudioVolume ${e}`);
        }
      }
      return _audioVolume;
    };

    const subscribeToAudioWithCallback = (pValue, reason, callback = () => {}) => {
      const value = castToBoolean(pValue, true);
      const promises = [];

      if (!reason && !_state.isSubscribing()) {
        addToPending(
          this,
          subscribeToAudioWithCallback,
          [value, reason, callback],
          () => callback(new Error('pending subscribeToAudio overridden'))
        );
        return;
      }

      const subs = getAllPeerConnections().map(async peerConnection =>
        (await peerConnection).subscribeToAudio(value));
      promises.push(...subs);
      if (_stream && getAllPeerConnections().length !== 0) {
        setChannelActiveStateForSubscriber({
          channelType: 'audio',
          activeState: value,
        });
      }

      const changed = _isSubscribingToAudio !== value;
      _isSubscribingToAudio = value;
      logAnalyticsEvent('subscribeToAudio', 'Attempt', { subscribeToAudio: value });
      const attemptStart = Date.now();

      if (changed) {
        setAudioVolume(value ? _latestPositiveVolume : 0);
      }

      if (_audioConnector.audioMediaProcessorConnector) {
        if (_isSubscribingToAudio) {
          promises.push(_setAudioMediaProcessorConnector());
        } else {
          try {
            _audioConnector.resetAudioStreamAndTrack();
          } catch (error) {
            logAnalyticsEvent('subscribeToAudio', 'Failure', {
              message: `Error resetting track: ${error.message}`,
            });
            callback(error);
            return;
          }
        }
      }

      Promise.all(promises)
        .then(() => {
          logAnalyticsEvent('subscribeToAudio', 'Success', { subscribeToAudio: value },
            { attemptDuration: Date.now() - attemptStart });
          callback();
        })
        .catch((err) => {
          logAnalyticsEvent('subscribeToAudio', 'Failure', { message: err?.message || String(err) },
            { attemptDuration: Date.now() - attemptStart });
          callback(err);
        });
    };

    /**
    * Toggles audio on and off. Starts subscribing to audio (if it is available and currently
    * not being subscribed to) when the <code>value</code> is <code>true</code>; stops
    * subscribing to audio (if it is currently being subscribed to) when the <code>value</code>
    * is <code>false</code>.
    * <p>
    * <i>Note:</i> This method only affects the local playback of audio. It has no impact on the
    * audio for other connections subscribing to the same stream. If the Publisher is not
    * publishing audio, enabling the Subscriber audio will have no practical effect.
    * </p>
    *
    * @param {Boolean} value Whether to start subscribing to audio (<code>true</code>) or not
    * (<code>false</code>).
    *
    * @return {Subscriber} The Subscriber object. This lets you chain method calls, as in the
    * following:
    *
    * <pre>mySubscriber.subscribeToAudio(true).subscribeToVideo(false);</pre>
    *
    * @see <a href="#subscribeToVideo">subscribeToVideo()</a>
    * @see <a href="Session.html#subscribe">Session.subscribe()</a>
    * @see <a href="StreamPropertyChangedEvent.html">StreamPropertyChangedEvent</a>
    *
    * @method #subscribeToAudio
    * @memberOf Subscriber
    */
    this.subscribeToAudio = (pValue, reason) => {
      subscribeToAudioWithCallback(pValue, reason);
      return this;
    };

    /**
     * Promise-based variant of {@link Subscriber#subscribeToAudio}.
     *
     * Toggles audio on or off and returns a Promise that settles once all underlying
     * peer-connection and audio-processor operations have completed.
     *
     * @param {Boolean} value - <code>true</code> to start subscribing to audio,
     *   <code>false</code> to stop.
     * @param {String} [reason] - Internal reason string (e.g. <code>'internal'</code>).
     *   Pass <code>undefined</code> for a user-initiated call.
     * @returns {Promise<void>} Resolves when audio has been successfully toggled;
     *   rejects with an <code>Error</code> if any operation fails.
     *
     * @example
     * // Mute audio and wait until the operation is fully complete
     * subscriber.subscribeToAudio.promise(false)
     *   .then(() => console.log('audio muted'))
     *   .catch(err => console.error('failed to mute audio', err));
     *
     * @method #subscribeToAudio.promise
     * @memberOf Subscriber
     */
    this.subscribeToAudio.promise = (pValue, reason) => new Promise((resolve, reject) => {
      subscribeToAudioWithCallback(pValue, reason, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    const reasonMap = {
      auto: 'quality',
      publishVideo: 'publishVideo',
      subscribeToVideo: 'subscribeToVideo',
    };

    if (env.name === 'Safari') {
      const onVisibilityChange = () => {
        if (!document.hidden && _properties.subscribeToVideo) {
          logging.debug('document visibility restored in Safari - resubscribing to video');
          this.subscribeToVideo(false, 'internal');
          this.subscribeToVideo(true, 'internal');
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);

      this.once('destroyed', () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      });
    }

    const subscribeToVideoWithCallback = async (pValue, reason, callback = () => {}) => {
      const enableVideo = castToBoolean(pValue, true);

      if (!reason && !_state.isSubscribing()) {
        addToPending(
          this,
          subscribeToVideoWithCallback,
          [enableVideo, reason, callback],
          () => callback(new Error('pending subscribeToVideo overridden'))
        );
        return;
      }

      // We restore to undefined to keep backward compatibility
      // eslint-disable-next-line no-param-reassign
      if (reason === 'internal') { reason = undefined; }

      if (!reason) {
        _isVideoManuallyMuted = !enableVideo;
      } else if (reason === 'auto') {
        updateCongestionLevel(enableVideo ? null : 2);
      }

      logAnalyticsEvent('subscribeToVideo', 'Attempt', { subscribeToVideo: enableVideo, reason });
      const attemptStart = Date.now();

      const promises = setAudioOnly(!enableVideo || !_stream.hasVideo || !_webRTCStream);

      if (_stream.hasVideo && !_streamHasTracks('video')) {
        if (enableVideo) {
          logging.info('Subscriber is audio-only due to incompatibility, can\'t subscribeToVideo.');
        }

        _properties.subscribeToVideo = false;
        const message = 'No video tracks available';
        logAnalyticsEvent('subscribeToVideo', 'Failure', { message });
        callback(new Error(message));
        return;
      }

      if (_chrome && _chrome.videoDisabledIndicator) {
        // If this is an auto disableVideo then we want to show the indicator, otherwise hide it again
        _chrome.videoDisabledIndicator.disableVideo(!enableVideo && reason === 'auto');
      }

      if (getAllPeerConnections().length > 0 && _session && _stream) {
        if (
          (enableVideo !== _properties.subscribeToVideo || reason !== _lastSubscribeToVideoReason)
          && !(_isVideoManuallyMuted && !!reason)
        ) {
          setChannelActiveStateForSubscriber({
            channelType: 'video',
            activeState: enableVideo,
            activeReason: reason,
          });
        }
      }

      _properties.subscribeToVideo = enableVideo;
      _lastSubscribeToVideoReason = reason;
      _videoEnabled = enableVideo;

      if (_videoMediaProcessorConnector) {
        if (_videoEnabled) {
          promises.push(_setVideoMediaProcessorConnector());
        } else {
          _resetVideoStream();
        }
      }

      if (reason !== 'loading') {
        this.dispatchEvent(new Events.VideoEnabledChangedEvent(
          enableVideo ? 'videoEnabled' : 'videoDisabled',
          {
            reason: reasonMap[reason] || 'subscribeToVideo',
          }
        ));
      }

      Promise.all(promises)
        .then(() => {
          logAnalyticsEvent('subscribeToVideo', 'Success', { subscribeToVideo: enableVideo, reason },
            { attemptDuration: Date.now() - attemptStart });
          callback();
        })
        .catch((err) => {
          logAnalyticsEvent('subscribeToVideo', 'Failure', { message: err?.message || String(err) });
          callback(err);
        });
    };

    /**
    * Toggles video on and off. Starts subscribing to video (if it is available and
    * currently not being subscribed to) when the <code>value</code> is <code>true</code>;
    * stops subscribing to video (if it is currently being subscribed to) when the
    * <code>value</code> is <code>false</code>.
    * <p>
    * <i>Note:</i> This method only affects the local playback of video. It has no impact on
    * the video for other connections subscribing to the same stream. If the Publisher is not
    * publishing video, enabling the Subscriber video will have no practical effect.
    * </p>
    *
    * @param {Boolean} value Whether to start subscribing to video (<code>true</code>) or not
    * (<code>false</code>).
    *
    * @return {Subscriber} The Subscriber object. This lets you chain method calls, as in the
    * following:
    *
    * <pre>mySubscriber.subscribeToVideo(true).subscribeToAudio(false);</pre>
    *
    * @see <a href="#subscribeToAudio">subscribeToAudio()</a>
    * @see <a href="Session.html#subscribe">Session.subscribe()</a>
    * @see <a href="StreamPropertyChangedEvent.html">StreamPropertyChangedEvent</a>
    *
    * @method #subscribeToVideo
    * @memberOf Subscriber
    */
    this.subscribeToVideo = (pValue, reason) => {
      subscribeToVideoWithCallback(pValue, reason);
      return this;
    };

    /**
     * Promise-based variant of {@link Subscriber#subscribeToVideo}.
     *
     * Toggles video on or off and returns a Promise that settles once all underlying
     * operations have completed (including any video media-processor connector update).
     *
     * @param {Boolean} value - <code>true</code> to start subscribing to video,
     *   <code>false</code> to stop.
     * @param {String} [reason] - Internal reason string (e.g. <code>'auto'</code>).
     *   Pass <code>undefined</code> for a user-initiated call.
     * @returns {Promise<void>} Resolves when video has been successfully toggled;
     *   rejects with an <code>Error</code> if any operation fails (e.g. no video tracks
     *   available, or the media-processor connector throws).
     *
     * @example
     * // Disable video and wait until the operation is fully complete
     * subscriber.subscribeToVideo.promise(false)
     *   .then(() => console.log('video disabled'))
     *   .catch(err => console.error('failed to disable video', err));
     *
     * @method #subscribeToVideo.promise
     * @memberOf Subscriber
     */
    this.subscribeToVideo.promise = (pValue, reason) => new Promise((resolve, reject) => {
      subscribeToVideoWithCallback(pValue, reason, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    /**
    * Sets the preferred resolution of the subscriber's video.
    * <p>
    * Lowering the preferred resolution
    * lowers video quality on the subscribing client, but it also reduces network and CPU usage.
    * You may want to use a lower resolution based on the dimensions of subscriber's video on
    * the web page. You may want to use a resolution rate for a subscriber to a stream that is less
    * important (and smaller) than other streams.
    * <p>
    * <p>
    * This method only applies when subscribing to a stream that uses the
    * <a href="https://tokbox.com/developer/guides/scalable-video">
    * scalable video feature</a>. Scalable video is available:
    * <ul>
    *   <li>
    *     Only in sessions that use the Vonage Video API Media Router (sessions with the
    *     <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media
    *     mode</a> set to routed).
    *   </li>
    *   <li>
    *     Only for streams published by clients that support scalable video:
    *     clients that use the Vonage Video API iOS SDK (on certain devices), the Vonage Video API
    *     Android SDK (on certain devices), or OpenTok.js in Chrome and Safari.
    *   </li>
    * </ul>
    * <p>
    * In streams that do not use scalable video, calling this method has no effect.
    * <p>
    * <b>Note:</b> The resolution for scalable video streams automatically adjusts for each
    * subscriber, based on network conditions and CPU usage, even if you do not call this method.
    * Call this method if you want to set a maximum resolution for this subscriber.
    * <p>
    * In streams that do not use scalable video, calling this method has no effect.
    * <p>
    * Not every frame rate is available to a subscriber. When you set the preferred resolution for
    * the subscriber, OpenTok.js picks the best resolution available that matches your setting.
    * The resolutions available are based on the value of the Subscriber object's
    * <code>stream.resolution</code> property, which represents the maximum resolution available for
    * the stream. The actual resolutions available depend, dynamically, on network and CPU resources
    * available to the publisher and subscriber.
    * <p>
    * You can set the initial preferred resolution used by setting the
    * <code>preferredResolution</code> property of the <code>options</code> object you pass into the
    * <code>Session.subscribe()</code> method.
    * <p>
    * <b>Note:</b> If you set the initial preferred resolution to <code>"auto"</code> when calling
    * <code>Session.subscribe()</code>, calling <code>setPreferredResolution()</code> fails silently,
    * and you will receive a warning.
    *
    * @param {Object} resolution Set this to an object with two properties: <code>width</code> and
    * <code>height</code> (both numbers), such as <code>{width: 320, height: 240}</code>. Set this to
    * <code>null</code> to remove the preferred resolution, and the client will use the highest
    * resolution available.
    *
    * @returns {Promise<void>} A promise that resolves when the preferred resolution has been
    * successfully applied, or rejects with an <code>Error</code> in the following conditions:
    * <ul>
    *   <li>The subscriber was initialized with <code>preferredResolution: 'auto'</code>.</li>
    *   <li>The value <code>'auto'</code> is passed directly to this method.</li>
    *   <li>The Subscriber is destroyed or not yet connected to a publisher.</li>
    *   <li>The session uses peer-to-peer media mode, which does not support this feature.</li>
    * </ul>
    *
    * @see <a href="#setPreferredFrameRate">Subscriber.setPreferredFrameRate()</a>
    * @see <a href="Session.html#subscribe">Session.subscribe()</a>
    *
    * @method #setPreferredResolution
    * @memberOf Subscriber
    */
    this.setPreferredResolution = preferredResolution => new Promise((resolve, reject) => {
      _logSetPreferredResolutionEvent('Attempt', { preferredResolution });
      if (_isAutoPreferredResolution) {
        const message = 'Cannot call this method directly if in auto mode.';
        _logSetPreferredResolutionEvent('Failure', { preferredResolution, message });
        logging.warn(message);
        reject(new Error(message));
        return;
      }

      if (preferredResolution === 'auto') {
        const message =
          "preferredResolution 'auto' cannot be set using setPreferredResolution(), it must be set in subscriberOptions property preferredResolution.";
        _logSetPreferredResolutionEvent('Failure', { preferredResolution, message });
        logging.warn(message);
        reject(new Error(message));
        return;
      }

      _setPreferredResolution(preferredResolution, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    const _logSetPreferredResolutionEvent = (variation, payload) => {
      if (!_isAutoPreferredResolution) {
        logAnalyticsEvent('setPreferredResolution', variation, payload);
      }
    };

    const _setPreferredResolution = (preferredResolution, callback = () => {}) => {
      this._preferredResolution = preferredResolution;
      if (_state.isDestroyed() || (getAllPeerConnections().length === 0 && _state.current !== 'Connecting')) {
        const message = 'Cannot set the max Resolution when not subscribing to a publisher';
        _logSetPreferredResolutionEvent('Failure', { preferredResolution, message });
        logging.warn(message);
        callback(new Error(message));
        return;
      }

      _properties.preferredResolution = preferredResolution;

      if (_session.sessionInfo.p2pEnabled) {
        const message = 'OT.Subscriber.setPreferredResolution will not work in a P2P Session';
        _logSetPreferredResolutionEvent('Failure', { preferredResolution, message });
        logging.warn(message);
        callback(new Error(message));
        return;
      }

      // We can only setPreferredResolution once the remote stream has been added. Otherwise we will save
      // the preferred resolution, and then it will be requested once the stream has been added.
      if (!_state.isStreamAdded()) {
        _logSetPreferredResolutionEvent('Pending');
        callback(); // resolve now or when the stream has been added?
        return;
      }

      const curMaxResolution = _stream.getPreferredResolution();

      const isUnchanged = (curMaxResolution && preferredResolution &&
        curMaxResolution.width === preferredResolution.width &&
        curMaxResolution.height === preferredResolution.height) ||
        (!curMaxResolution && !preferredResolution);

      if (isUnchanged) {
        _logSetPreferredResolutionEvent('Cancel');
        callback();
        return;
      }

      try {
        _stream.setPreferredResolution(preferredResolution, (err) => {
          if (err) {
            _logSetPreferredResolutionEvent('Failure', { preferredResolution, message: err.message });
            callback(err);
          } else {
            _logSetPreferredResolutionEvent('Success');
            callback();
          }
        });
      } catch (err) {
        callback(err);
        throw err;
      }
    };

    const _setOptimizedResolution = debounce((element) => {
      if (!element) {
        return;
      }
      const videoElementWidth = element.offsetWidth;
      const videoElementHeight = element.offsetHeight;
      const deviceRatio = window.devicePixelRatio;

      const updatedPreferredResolution = {
        width: videoElementWidth * deviceRatio,
        height: videoElementHeight * deviceRatio,
      };
      _setPreferredResolution(updatedPreferredResolution);
    }, RESIZE_DEBOUNCE_WAIT);

    /**
    * Sets the preferred frame rate of the subscriber's video.
    * <p>
    * Lowering the preferred frame rate
    * lowers video quality on the subscribing client, but it also reduces network and CPU usage.
    * You may want to use a lower frame rate for a subscriber to a stream that is less important
    * than other streams.
    * <p>
    * <p>
    * This method only applies when subscribing to a stream that uses the
    * <a href="https://tokbox.com/developer/guides/scalable-video">
    * scalable video feature</a>. Scalable video is available:
    * <ul>
    *   <li>
    *     Only in sessions that use the Vonage Video API Media Router (sessions with the
    *     <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media
    *     mode</a> set to routed).
    *   </li>
    *   <li>
    *     Only for streams published by clients that support scalable video:
    *     clients that use the Vonage Video API iOS SDK (on certain devices), the Vonage Video API
    *     Android SDK (on certain devices), or OpenTok.js in Chrome and Safari.
    *   </li>
    * </ul>
    * <p>
    * In streams that do not use scalable video, calling this method has no effect.
    * <p>
    * <b>Note:</b> The frame rate for scalable video streams automatically adjusts for each
    * subscriber, based on network conditions and CPU usage, even if you do not call this method.
    * Call this method if you want to set a maximum frame rate for this subscriber.
    * <p>
    * Not every frame rate is available to a subscriber. When you set the preferred frame rate for
    * the subscriber, OpenTok.js picks the best frame rate available that matches your setting.
    * The frame rates available are based on the value of the Subscriber object's
    * <code>stream.frameRate</code> property, which represents the maximum value available for the
    * stream. The actual frame rates available depend, dynamically, on network and CPU resources
    * available to the publisher and subscriber.
    * <p>
    * You can set the initial preferred frame rate used by setting the <code>preferredFrameRate</code>
    * property of the <code>options</code> object you pass into the <code>Session.subscribe()</code>
    * method.
    *
    * @param {Number} frameRate Set this to the desired frame rate (in frames per second). Set this to
    * <code>null</code> to remove the preferred frame rate, and the client will use the highest
    * frame rate available.
    *
    * @returns {Promise<void>} A promise that resolves when the preferred frame rate has been
    * successfully applied, or rejects with an <code>Error</code> in the following conditions:
    * <ul>
    *   <li>The Subscriber is destroyed or not yet connected to a publisher.</li>
    *   <li>The session uses peer-to-peer media mode, which does not support this feature.</li>
    *   <li>The underlying stream update fails.</li>
    * </ul>
    *
    * @see <a href="#setPreferredResolution">Subscriber.setPreferredResolution()</a>
    * @see <a href="Session.html#subscribe">Session.subscribe()</a>
    *
    * @method #setPreferredFrameRate
    * @memberOf Subscriber
    */
    this.setPreferredFrameRate = preferredFrameRate => new Promise((resolve, reject) => {
      this._preferredFrameRate = preferredFrameRate;
      if (_state.isDestroyed() || (getAllPeerConnections().length === 0 && _state.current !== 'Connecting')) {
        const msg = 'Cannot set the max frameRate when not subscribing to a publisher';
        logging.warn(msg);
        reject(new Error(msg));
        return;
      }

      _properties.preferredFrameRate = preferredFrameRate;

      if (_session.sessionInfo.p2pEnabled) {
        const msg = 'OT.Subscriber.setPreferredFrameRate will not work in a P2P Session';
        logging.warn(msg);
        reject(new Error(msg));
        return;
      }
      const currentPreferredFrameRate = _stream.getPreferredFrameRate();

      const isUnchangedFrameRate = (preferredFrameRate && currentPreferredFrameRate &&
      (currentPreferredFrameRate === preferredFrameRate)) ||
      (!currentPreferredFrameRate && !preferredFrameRate);

      if (isUnchangedFrameRate) {
        resolve();
        return;
      }

      try {
        _stream.setPreferredFrameRate(preferredFrameRate, (err) => {
          if (err) {
            reject(err);
          } else {
            resolve();
          }
        });
      } catch (err) {
        reject(err);
        throw err;
      }
    });

    this.isSubscribing = () => _state.isSubscribing();

    this.isWebRTC = true;

    this.isLoading = () => _widgetView && _widgetView.loading();

    /**
     * Indicates whether the subscriber's audio is blocked because of
     * the browser's audio autoplay policy.
     *
     * @see <a href="OT.html#unblockAudio">OT.unblockAudio()</a>
     * @see The <a href="#event:audioBlocked">audioBlocked</a>
     * and <a href="#event:audioUnblocked">audioUnblocked</a>
     * Subscriber events
     * @see The <code>style.audioBlockedDisplayMode</code> property of the
     * <code>options</code> parameter of the
     * <a href="Session.html#subscribe">Session.subscribe()</a> method
     *
     * @method #isAudioBlocked
     * @memberof Subscriber
     */
    this.isAudioBlocked = () => Boolean(_widgetView && _widgetView.isAudioBlocked());

    this.videoElement = () => {
      const video = _widgetView && _widgetView.video();
      return video ? video.domElement() : null;
    };

    /**
    * Returns the actual received width, in pixels, of the Subscriber video. This reflects
    * the decoded resolution of the incoming video stream, which may differ from the
    * publisher's capture resolution due to network/CPU adaptation or scalable video layer
    * selection. Use this method to observe the real resolution being rendered for this
    * subscriber.
    * <p>
    * For more information on monitoring stream quality, see the
    * <a href="https://tokbox.com/developer/guides/client-observability/">Client Observability</a>
    * developer guide.
    *
    * @see <a href="#getStats">Subscriber.getStats()</a>
    * @method #videoWidth
    * @memberOf Subscriber
    * @return {Number} The actual received width, in pixels, of the Subscriber video.
    */
    this.videoWidth = () => {
      const video = _widgetView && _widgetView.video();
      return video ? video.videoWidth() : undefined;
    };

    /**
    * Returns the actual received height, in pixels, of the Subscriber video. This reflects
    * the decoded resolution of the incoming video stream, which may differ from the
    * publisher's capture resolution due to network or cpu adaptation or scalable video layer
    * selection. Use this method to observe the real resolution being rendered for this
    * subscriber.
    * <p>
    * For more information on monitoring stream quality, see the
    * <a href="https://tokbox.com/developer/guides/client-observability/">Client Observability</a>
    * developer guide.
    *
    * @see <a href="#getStats">Subscriber.getStats()</a>
    * @method #videoHeight
    * @memberOf Subscriber
    * @return {Number} The actual received height, in pixels, of the Subscriber video.
    */
    this.videoHeight = () => {
      const video = _widgetView && _widgetView.video();
      return video ? video.videoHeight() : undefined;
    };

    this._subscribeToSelf = () => {
      const publisher = _session.getPublisherForStream(_stream);
      if (!(publisher && publisher._.webRtcStream())) {
        connectivityState.fail({
          payload: {
            reason: 'streamNotFound',
          },
          error: otError(
            errors.STREAM_DESTROYED,
            new Error('Tried to subscribe to a local publisher, but its stream no longer exists')
          ),
        });
      } else {
        _pcConnected.resolve();

        onRemoteStreamAdded.push({ webRTCStream: publisher._.webRtcStream() });
      }
    };

    this._setupPeerConnection = async ({ send, log, logQoS, sourceStreamId, peerId }) => {
      if (_properties.testNetwork) {
        setAudioVolume(0);
      }

      if (getAllPeerConnections().length === 0) {
        // @todo The subscribers states should be something like:
        // disconnected || connecting || reconnecting || connected || destroyed
        // the state of peer connections belong to the peer connection itself
        _state.set('Connecting');
      }
      _activeSourceStreamId = sourceStreamId;
      networkConditionChecker.setSourceStreamId(_activeSourceStreamId);
      peerConnectionsAsync[sourceStreamId] = new Promise((resolve, reject) => {
        _session._.getIceConfig()
          .then((iceConfig) => {
            if (iceConfig.needRumorIceServersFallback) {
              iceConfig.servers = [...(fallbackIceServers || []), ...(iceConfig.servers || [])];
            }
            const props = {
              iceConfig,
              subscriberId: this.widgetId,
              send,
              logAnalyticsEvent: log,
              p2p: _session.sessionInfo.p2pEnabled,
              sourceStreamId,
              remoteConnectionId: _stream.connection.id,
              // We force to use the SPC only for Mantis leg.
              _singlePeerConnectionController: sourceStreamId === 'MANTIS' &&
                _session._singlePeerConnectionController,
              keyStore: _session.keyStore,
              sFrameClientStore: _session.sFrameClientStore,
              isE2ee: _session._.isE2ee(),
              sessionId: _session?.sessionId,
            };

            if (Object.prototype.hasOwnProperty.call(_properties, 'codecFlags')) {
              props.codecFlags = _properties.codecFlags;
            }
            const peerConnection = new Subscriber.SubscriberPeerConnection(props);
            peerConnection.once('iceConnected', _pcConnected.resolve);
            peerConnection.once('error', _pcConnected.reject);
            peerConnection.on('qos', logQoS);

            setPeerConnectionEvents(peerConnection, sourceStreamId, peerId);

            // initialize the peer connection AFTER we've added the event listeners
            peerConnection.init((err) => {
              if (err) {
                reject(err);
              } else {
                resolve(peerConnection);
              }
            });
          });
      });
      return peerConnectionsAsync[sourceStreamId];
    };

    const restrictFrameRateWithCallback = (val, callback = () => {}) => {
      logging.debug(`OT.Subscriber.restrictFrameRate(${val})`);

      logAnalyticsEvent('restrictFrameRate', String(val), { streamId: _stream.id });

      if (_session.sessionInfo.p2pEnabled) {
        logging.warn('OT.Subscriber.restrictFrameRate: Cannot restrictFrameRate on a P2P session');
      }

      if (typeof val !== 'boolean') {
        const msg = `OT.Subscriber.restrictFrameRate: expected a boolean value got a ${typeof val}`;
        logging.error(msg);
        callback(new Error(msg));
      } else {
        _frameRateRestricted = val;
        _stream.setRestrictFrameRate(val, callback);
      }
    };

    /**
    * Restricts the frame rate of the Subscriber's video stream, when you pass in
    * <code>true</code>. When you pass in <code>false</code>, the frame rate of the video stream
    * is not restricted.
    * <p>
    * When the frame rate is restricted, the Subscriber video frame will update once or less per
    * second.
    * <p>
    * This feature is only available in sessions that use the Vonage Video API Media Router (sessions with
    * the <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media mode</a>
    * set to routed), not in sessions with the media mode set to relayed. In relayed sessions,
    * calling this method has no effect.
    * <p>
    * Restricting the subscriber frame rate has the following benefits:
    * <ul>
    *    <li>It reduces CPU usage.</li>
    *    <li>It reduces the network bandwidth consumed.</li>
    *    <li>It lets you subscribe to more streams simultaneously.</li>
    * </ul>
    * <p>
    * Reducing a subscriber's frame rate has no effect on the frame rate of the video in
    * other clients.
    *
    * @param {Boolean} value Whether to restrict the Subscriber's video frame rate
    * (<code>true</code>) or not (<code>false</code>).
    *
    * @return {Subscriber} The Subscriber object. This lets you chain method calls, as in the
    * following:
    *
    * <pre>mySubscriber.restrictFrameRate(false).subscribeToAudio(true);</pre>
    *
    * @method #restrictFrameRate
    * @memberOf Subscriber
    */
    this.restrictFrameRate = (val) => {
      restrictFrameRateWithCallback(val);
      return this;
    };

    /**
     * Promise-based variant of {@link Subscriber#restrictFrameRate}.
     *
     * Restricts or unrestricts the frame rate of the Subscriber's video stream and returns
     * a Promise that settles once the underlying stream operation has completed.
     *
     * @param {Boolean} value - <code>true</code> to restrict the frame rate to approximately
     *   one frame per second; <code>false</code> to remove the restriction.
     * @returns {Promise<void>} Resolves when the frame-rate restriction has been applied;
     *   rejects with an <code>Error</code> if the operation fails (for example, if the
     *   underlying stream update is rejected).
     *
     * @example
     * subscriber.restrictFrameRate.promise(true)
     *   .then(() => console.log('frame rate restricted'))
     *   .catch(err => console.error('failed to restrict frame rate', err));
     *
     * @method #restrictFrameRate.promise
     * @memberOf Subscriber
     */
    this.restrictFrameRate.promise = val => new Promise((resolve, reject) => {
      restrictFrameRateWithCallback(val, (err) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      });
    });

    this.on('styleValueChanged', updateChromeForStyleChange, this);

    this._ = {
      async getDataChannel(label, options, completion) {
        // @fixme this will fail if it's called before we have a SubscriberPeerConnection.
        // I.e. before we have a publisher connection.
        if (!getCurrentPeerConnection()) {
          completion(
            new OTHelpers.Error('Cannot create a DataChannel before there is a publisher connection.')
          );

          return;
        }
        (await getCurrentPeerConnection()).getDataChannel(label, options, completion);
      },

      async iceRestart(reason, forcedRestart = false) {
        if (!_session._.isSocketConnected()) {
          logging.debug('Subscriber: Skipping ice restart, websocket is not connected');
          return;
        }
        if (_session.sessionInfo.p2pEnabled) {
          // ICERestart is performed by the Publisher in P2P
          return;
        }
        // Either Routed or AMR sessions, we only iceRestart Mantis PC
        const peerConnection = await getPeerConnectionBySourceStreamId('MANTIS');

        if (!peerConnection) {
          logging.debug('Subscriber: Skipping ice restart, we have no peer connection');
          return;
        }

        logging.debug('Subscriber: iceRestart attempt');
        peerConnection.iceRestart(reason, forcedRestart);
      },

      unblockAudio: () => _widgetView && _widgetView.unblockAudio(),
      webRtcStream: () => _webRTCStream,

      setIceConfig: async (iceConfig) => {
        getAllPeerConnections().forEach(async pc => (await pc).setIceConfig(iceConfig));
      },

      privateEvents: new EventEmitter(),

      startRoutedToRelayedTransition() {
        if (_properties.testNetwork || isLocalStream(_stream, _session)) {
          // We avoid transitioning to relayed when doing a network test because we need
          // to subscribe from Mantis, or in case we're subscribing to the local stream.
          return;
        }
        amrState.startTransitionToRelayed();
        _transitionRoutedToRelayed();
      },

      startRelayedToRoutedTransition() {
        amrState.startTransitionToRouted(_transitionRelayedToRouted);
      },

      async startMigration() {
        if (_state.isMigrating()) {
          return;
        }

        _state.set('Migrating');
        logAnalyticsEvent('SessionMigration', 'Attempt');

        startMigrationTimeout();

        socket.off('signalingNetworkQualityScoreChanged', networkConditionChecker.checkNetworkConditionChange);
        // Update socket reference
        socket = _session._.getSocket();
        socket.on('signalingNetworkQualityScoreChanged', networkConditionChecker.checkNetworkConditionChange);

        if (_session.sessionInfo.p2pEnabled) {
          subscriberCreate('P2P');
        } else {
          _previousPeerConnection = await getPeerConnectionBySourceStreamId('MANTIS');
          subscriberCreate('MANTIS');
        }
      },

      async finishMigration() {
        const destroyPeerConnection = async (pc, sourceStreamId) => {
          if (pc) {
            await _subscriberStatsHelper.removePeerConnection(pc);
            pc.close();
            delete peerConnectionsAsync[sourceStreamId];
          }
        };

        // Clear previous PC
        // We don't pass 'MANTIS' because we don't want to delete the new MANTIS peer connection
        destroyPeerConnection(_previousPeerConnection);
        _previousPeerConnection = null;

        // In case we don't transition to P2P after migration, clear P2P leg.
        if (_isAdaptiveEnabled && _activeSourceStreamId === 'MANTIS') {
          getPeerConnectionBySourceStreamId('P2P')?.then(pc => destroyPeerConnection(pc, 'P2P'));
        }

        setStateToSubscribing();
        clearTimeout(_migrationTimeoutId);
        logAnalyticsEvent('SessionMigration', 'Success');
      },

      setSinkId(deviceId) {
        const video = _widgetView && _widgetView.video();
        if (video) {
          return _widgetView.setSinkId(deviceId);
        }
        logging.debug('Called setAudioOutputDevice on a subscriber without a video element');
        return undefined;
      },

      pauseAndPlayVideoElement() {
        if (_widgetView) {
          _widgetView.pauseAndPlayVideoElement();
        }
      },
      get isSubscribingToVideo() {
        return _properties.subscribeToVideo;
      },
      getActiveSourceStreamId() {
        return _activeSourceStreamId;
      },
      getNetworkConditionStats() {
        return _networkConditionStats;
      },
    };

    this._onSenderStatsReceived = (content) => {
      networkConditionChecker.onSenderStatsReceived(content);
    };

    _state = new SubscribingState(stateChangeFailed);

    const setStateToSubscribing = () => {
      _state.set('Subscribing');
      // execute pending tasks now that the Subscirber is connected
      executePending(this);
    };

    logging.debug(`OT.Subscriber: subscribe to ${_stream.id}`);

    _state.set('Init');

    if (!_stream) {
      // @todo error
      logging.error('OT.Subscriber: No stream parameter.');
      return false;
    }

    _streamEventHandlers = {
      updated: streamUpdated,
    };

    _stream.on(_streamEventHandlers, this);

    _properties.name = _properties.name || _stream.name;
    _properties.classNames = 'OT_root OT_subscriber';

    if (_properties.style) {
      this.setStyle(_properties.style, null, true);
    }

    _latestPositiveVolume = DEFAULT_AUDIO_VOLUME;
    _properties.subscribeToVideo = castToBoolean(_properties.subscribeToVideo, true);
    _isVideoManuallyMuted = !_properties.subscribeToVideo;
    _properties.subscribeToAudio = castToBoolean(_properties.subscribeToAudio, true);
    this.subscribeToAudio(_properties.subscribeToAudio, 'internal');

    setAudioVolume(determineAudioVolume(_properties));

    _widgetView = new Subscriber.WidgetView(targetElement, { ..._properties, widgetType: 'subscriber' });
    _widgetView.on('error', onVideoError);
    _widgetView.on('audioBlocked', () => this.trigger('audioBlocked'));
    _widgetView.on('audioUnblocked', () => this.trigger('audioUnblocked'));
    // We now log video transitions for AMR. These action are being triggered and logged
    // at this level.
    _widgetView.on('bindEvent', (action, variation, options) => {
      logAnalyticsEvent(action, variation, {}, options);
    });

    this.id = _widgetView.domId();
    _domId = _widgetView.domId();
    this.element = _widgetView.domElement;

    _widgetView.on('videoElementCreated', (element) => {
      const event = new Events.VideoElementCreatedEvent(element);
      const self = this;
      if (!_loaded) {
        this.once('loaded', () => {
          self.dispatchEvent(event);
        });
      } else {
        this.dispatchEvent(event);
      }

      if (_isAutoPreferredResolution && element) {
        if (element.height !== 0 && element.width !== 0) {
          _setOptimizedResolution(element);
        }
        _resizeObserver = new ResizeObserverPolyfill(() => {
          _setOptimizedResolution(element);
        });
        _resizeObserver.observe(element);
      }
    });

    _widgetView.on('destroy', () => {
      _resizeObserver?.disconnect();
    });

    if (this.element) {
      // Only create the chrome if there is an element to insert it in
      // for insertDefautlUI:false we don't create the chrome
      _createChrome.call(this);
    }

    const audio = _stream.getChannelsOfType('audio');
    const video = _stream.getChannelsOfType('video');
    const data = _stream.getChannelsOfType('data');
    let shouldSubscribeToDownlinkStatsChannel;
    let isSubscribedToDownlinkStats = false;

    (function checkDownlinkStatsChannel() {
      const downlinkStatsChannel = data.find(channel => channel.id === 'downlinkStats1');
      shouldSubscribeToDownlinkStatsChannel = !!downlinkStatsChannel;
      if (downlinkStatsChannel?.active) {
        downlinkStatsChannel.update({ active: false });
      }
    }());

    // If the user has not specified a value for subscribeToCaptions, we will default to the stream
    // caption channel active state.
    _properties.subscribeToCaptions =
      _properties.subscribeToCaptions ?? data.some(channel => channel.id === 'caption1' && channel.active);

    /**
    * Toggles captions on and off. Starts subscribing to captions (if it is available and currently
    * not being subscribed to) when the <code>value</code> is <code>true</code>; stops
    * subscribing to captions (if it is currently being subscribed to) when the <code>value</code>
    * is <code>false</code>.
    * <p>
    * The initial value can be set by setting the <code>subscribeToCaptions</code>
    * property of the <code>options</code> parameter of the <a href="Session.html#subscribe">Session.subscribe()</a>
    * method. The default value is the <code>hasCaptions</code> property of the stream’s publisher at the time of
    * subscriber initialization (see the
    * <a href="https://tokbox.com/developer/sdks/js/reference/Stream.html#properties">Stream.hasCaptions property</a>).
    * <p>
    * For more information, see the
    * <a href="https://tokbox.com/developer/guides/live-captions/">Live Captions</a>
    * developer guide.
    *
    * @param {Boolean} value Whether to start subscribing to captions (<code>true</code>) or not
    * (<code>false</code>).
    *
    * @return {Promise<void>} A promise that resolves when the server has acknowledged the
    * caption channel update. The promise is rejected if an error occurs while sending the update.
    *
    * @see <a href="#isSubscribedToCaptions">isSubscribedToCaptions()</a>
    * @see <a href="#event:captionReceived">captionReceived</a> event
    * @see <a href="StreamPropertyChangedEvent.html">StreamPropertyChangedEvent</a>
    * @see <a href="Session.html#subscribe">Session.subscribe()</a>
    *
    * @method #subscribeToCaptions
    * @memberOf Subscriber
    */
    this.subscribeToCaptions = value => new Promise((resolve, reject) => {
      try {
        const active = castToBoolean(value, true);
        const attributes = {
          active,
        };
        _properties.subscribeToCaptions = active;
        logAnalyticsEvent('subscribeToCaptions', 'Attempt', { subscribeToCaptions: active });

        socket.subscriberChannelUpdate(_stream.id, this.widgetId, 'caption1', attributes, ((err) => {
          if (err) {
            logAnalyticsEvent('subscribeToCaptions', 'Failure', { message: err?.message || String(err) });
            reject(err);
          } else {
            logAnalyticsEvent('subscribeToCaptions', 'Success', { subscribeToCaptions: active });
            resolve();
          }
        }));
      } catch (err) {
        logAnalyticsEvent('subscribeToCaptions', 'Failure', { message: err?.message || String(err) });
        reject(err);
      }
    });

    /**
     * Sets the preferred language for captions translation for this subscriber.
     *
     * <p>
     * <i>This is a private beta feature.</i>
     *
     * @param {CaptionsLanguageCode | null} langCode - The language code to translate captions into.
     *        Pass <code>null</code> to use the default language of session (no translation).
     *
     * @method #setCaptionsTranslationLanguage
     * @memberOf Subscriber
     */
    this.setCaptionsTranslationLanguage = async (langCode) => {
      if (langCode !== null && !validateLanguageCode(langCode)) {
        const message = `Invalid captions translation language ${langCode}`;
        logging.warn(`setCaptionsTranslationLanguage: ${message}`);
        throw otError(
          Errors.INVALID_PARAMETER,
          new Error(`setCaptionsTranslationLanguage: ${message}`)
        );
      }
      _properties.captionsTranslationLanguage = langCode;
      const language = langCode === null ? '' : langCode;
      const attributes = {
        category: language,
      };

      socket.subscriberChannelUpdate(_stream.id, this.widgetId, 'caption1', attributes);
      logAnalyticsEvent('setCaptionsTranslationLanguage', 'Event', { captionsTranslationLanguage: langCode });
    };

    /**
     * Returns the current captions translation language code for this subscriber.
     * If no translation language is set, returns <code>null</code> to indicate the default language is used.
     *
     * <p>
     * <i>This is a private beta feature.</i>
     *
     * @returns {CaptionsLanguageCode | null} The language code currently set for
     * captions translation, or <code>null</code> if default.
     *
     * @method #getCaptionsTranslationLanguage
     * @memberOf Subscriber
     *
     */
    this.getCaptionsTranslationLanguage = () => _properties.captionsTranslationLanguage;

    /**
     * Whether the subscriber is receiving captions. This property can change if the subscriber turns captioning on
     * or off (by calling the <code>Subscriber.subscribeToCaptions()</code>
     * method). The default value is
     * the <code>hasCaptions</code> property of the stream’s publisher at the time of subscriber initialization (see the
     * <a href="https://tokbox.com/developer/sdks/js/reference/Stream.html#properties">Stream.hasCaptions property</a>).
     * <p>
     * You can set the initial value of this property by setting the <code>subscribeToCaptions</code>
     * property of the <code>options</code> parameter of the <a href="Session.html#subscribe">Session.subscribe()</a>
     * method.
     *
     * @class Subscriber
     * @see <a href="#subscribeToCaptions">Subscriber.subscribeToCaptions()</a>
     * @see <a href="Session.html#subscribe">Session.subscribe()</a>
     *
     * @method #isSubscribedToCaptions
     * @memberOf Subscriber
     */
    this.isSubscribedToCaptions = () => _properties.subscribeToCaptions;

    const channelsToSubscribeTo = audio.map(channel => ({
      id: channel.id,
      type: channel.type,
      active: _properties.subscribeToAudio,
    })).concat(video.map((channel) => {
      const props = {
        id: channel.id,
        type: channel.type,
        active: _properties.subscribeToVideo,
        restrictFrameRate: _properties.restrictFrameRate !== undefined ?
          _properties.restrictFrameRate : false,
      };

      if (_properties.preferredFrameRate !== undefined) {
        props.preferredFrameRate = parseFloat(_properties.preferredFrameRate);
        this._preferredFrameRate = props.preferredFrameRate;
      }

      if (_properties.preferredHeight !== undefined) {
        props.preferredHeight = parseInt(_properties.preferredHeight, 10);
      }

      if (_properties.preferredWidth !== undefined) {
        props.preferredWidth = parseInt(_properties.preferredWidth, 10);
      }

      return props;
    })).concat(data.map(channel => ({
      id: channel.id,
      type: channel.type,
      active: channel.active,
    })));

    const publisherCaptionsActive = _stream.getChannelsOfType('data')[0]?.active;
    const shouldSubscribeToSelf = !_properties.testNetwork && isLocalStream(_stream, _session) && !publisherCaptionsActive;

    const messageRumorWhenPublisherHasCaptionsListener = () => {
      // In the case that a publisher decides to publish captions when we are self subscribing, we will want to
      // inform rumor of the subscriber's existence via subscriberCreate
      const publisher = _session.getPublisherForStream(_stream);
      if (publisher?._?.privateEvents && shouldSubscribeToSelf) {
        // We only want to call subscriberCreate once as multiple calls will cause an error
        // and we can't forsee if the publisher will toggle before the subscriber is created
        let didSubscriberCreate = false;
        publisher._.privateEvents.on('publisherCaptionsEnabled', (event) => {
          if (!didSubscriberCreate && event.captionsEnabled) {
            channelsToSubscribeTo.find(channel => channel.id === 'caption1').active = true;
            subscriberCreate();
            didSubscriberCreate = true;
          }
        });
      }
    };

    messageRumorWhenPublisherHasCaptionsListener();

    const subscriberCreate = (sourceStreamId = null) => {
      if (!_state.isMigrating()) {
        connectivityState.beginConnect();
      }
      socket.subscriberCreate(
        _stream.id,
        _widgetId, // subscriberId
        channelsToSubscribeTo,
        sourceStreamId,
        _session._.isE2ee(),
        (err, message) => {
          const payload = {
            status: message?.status,
            ...(err?.message && { error: err?.message }),
          };
          logAnalyticsEvent('subscriberCreate', 'Event', payload);
          // when the publisher is destroyed before we subscribe, chances are we have been told about
          // before we get the subscriberCreate error, so this can be ignored.
          if (err && !connectivityState.is('disconnected')) {
            onSubscriberCreateError(err);
          }
          fallbackIceServers = parseIceServers(message);
        }
      );
    };

    if (shouldSubscribeToSelf) {
      connectivityState.beginConnect();
      // bypass rumor etc and just subscribe locally
      this._subscribeToSelf();
      return this;
    }

    subscriberCreate();

    /**
     * Sets a MediaProcessorConnector for the subscriber video track.
     * A subscriber can have only one video
     * <a href="https://vonage.github.io/media-processor-docs/docs/api/classes/MediaProcessorConnector">MediaProcessorConnector</a>
     * applied at one time.  When you set a new
     * connector, the previously set video connector is removed.
     *
     * <p>
     * <em>Note:</em> MediaProcessors are only supported in recent versions of Chrome, Firefox, Electron, Opera,
     * Edge, Samsung Internet, WebView Android, and the latest versions of Safari and iOS WebView.
     * You can check if the client supports this feature by calling the
     * <a href="OT.html#hasMediaProcessorSupport">OT.hasMediaProcessorSupport('video')</a> method.
     * </p>
     *
     * <p>
     * Calling this method results in an error (the Promise returned by the method is rejected)
     * in the following conditions:
     * <ul>
     *   <li>
     *     There are no video input devices (cameras) available.
     *   </li>
     *   <li>
     *     There was an error acquiring video from the video input device.
     *   </li>
     *   <li>
     *     There was an error acquiring the processed track from the MediaProcessorConnector.
     *   </li>
     *  </ul>
     * </p>
     *
     * <p>
     * You can clear the current connector by calling this method with <code>null</code>.
     *
     * <p>
     * For more information, see the
     * <a href="https://tokbox.com/developer/guides/vonage-media-processor/">
     * Using the Vonage Media Processor library</a>
     * developer guide.
     *
     * @param {MediaProcessorConnector} mediaProcessorConnector A MediaProcessorConnector instance.
     * See the <a href="https://vonage.github.io/media-processor-docs/docs/intro/">Vonage Media Processor
     * library docs</a>.</td>
     *
     * @method #setVideoMediaProcessorConnector
     * @memberOf Subscriber
     *
     * @see <a href="OT.html#hasMediaProcessorSupport">OT.hasMediaProcessorSupport('video')</a>
     *
     * @return {Promise} A promise that resolves when the operation completes successfully.
     * If there is an error, the promise is rejected and no connector is set.
     */
    this.setVideoMediaProcessorConnector = async (mediaProcessorConnector) => {
      logAnalyticsEvent('setVideoMediaProcessorConnector', 'Attempt', { message: mediaProcessorConnector ? 'setting the connector' : 'clearing the connector' });
      _clearConnector();
      if (!mediaProcessorConnector) {
        _resetVideoStream();
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Success', { message: 'clearing the connector' });
        return;
      }

      if (!MediaProcessorConnector.isValidConnector(mediaProcessorConnector)) {
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Failure', { message: 'Invalid MediaProcessorConnector' });
        throw otError(
          Errors.INVALID_PARAMETER,
          new Error('setVideoMediaProcessorConnector:  Tried to set invalid MediaProcessorConnector')
        );
      }

      _videoMediaProcessorConnector = new MediaProcessorConnector(mediaProcessorConnector);

      // this is needed to avoid double filtering during AMR transitions
      const isTransitioning = amrState.isTransitioning();
      if (isTransitioning || !_videoEnabled) {
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Pending', {
          isTransitioning,
          isVideoEnabled: _videoEnabled,
        });
        return;
      }

      _resetVideoStream();

      try {
        await _setVideoMediaProcessorConnector();
      } catch (err) {
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Failure', { message: err.message });
        throw err;
      }
    };

    const _setVideoMediaProcessorConnector = async () => {
      [_originalVideoTrack] = _webRTCStream.getVideoTracks();

      if (!_originalVideoTrack) {
        const message = 'Connector not set as no video is present.';
        logging.warn(`setVideoMediaProcessorConnector: ${message}`);
        _videoMediaProcessorConnector = null;
        throw Error('setVideoMediaProcessorConnector', 'Failure', { message });
      }

      try {
        const filteredVideoTrack = await _videoMediaProcessorConnector.setTrack(_originalVideoTrack);
        updateTrackOnStream(_webRTCStream, _originalVideoTrack, filteredVideoTrack);
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Success');
      } catch (err) {
        _videoMediaProcessorConnector = null;
        logging.error(`setVideoMediaProcessorConnector: Error getting track from MediaProcessorConnector: ${err}`);
        throw Error('setVideoMediaProcessorConnector', 'Failure', { message: err.message });
      }
    };

    const _resetVideoStream = () => {
      if (!_originalVideoTrack) {
        return;
      }

      try {
        // We need to switch to the original track so we can transform it later when resetting the connector.
        const [filteredVideoTrack] = _webRTCStream.getVideoTracks();
        updateTrackOnStream(_webRTCStream, filteredVideoTrack, _originalVideoTrack);
        logAnalyticsEvent('setVideoMediaProcessorConnector', 'Success', { message: 'reset the video track' });
      } catch (err) {
        logging.error(`setVideoMediaProcessorConnector: Error resetting track: ${err}`);
        throw Error('setVideoMediaProcessorConnector', 'Failure', { message: err.message });
      }

      _originalVideoTrack = null;
    };

    const _clearConnector = () => {
      _videoMediaProcessorConnector?.destroy();
      _videoMediaProcessorConnector = null;
    };

    /**
     * Sets a MediaProcessorConnector for the subscriber audio track.
     * A subscriber can have only one audio
     * <a href="https://vonage.github.io/media-processor-docs/docs/api/classes/MediaProcessorConnector">MediaProcessorConnector</a>
     * applied at one time.  When you set a new
     * connector, the previously set audio connector is removed.
     *
     * <p>
     * <em>Note:</em> MediaProcessors are only supported in recent versions of Chrome, Electron, Opera, Edge, Samsung Internet,
     * and WebView Android.
     * They are not supported in other (non-Chromium-based) browsers or on iOS. You can check if the client
     * supports this feature by calling the
     * <a href="OT.html#hasMediaProcessorSupport">OT.hasMediaProcessorSupport('audio')</a> method.
     * </p>
     *
     * <p>
     * Calling this method results in an error (the Promise returned by the method is rejected)
     * in the following conditions:
     * <ul>
     *   <li>
     *     There was an error acquiring the processed track from the MediaProcessorConnector.
     *   </li>
     *  </ul>
     * </p>
     *
     * <p>
     * You can clear the current connector by calling this method with <code>null</code>.
     *
     * <p>
     * For more information, see the
     * <a href="https://tokbox.com/developer/guides/vonage-media-processor/">
     * Using the Vonage Media Processor library</a>
     * developer guide.
     *
     * @param {MediaProcessorConnector} mediaProcessorConnector A MediaProcessorConnector instance.
     * See the <a href="https://vonage.github.io/media-processor-docs/docs/intro/">Vonage Media Processor
     * library docs</a>.</td>
     *
     * @method #setAudioMediaProcessorConnector
     * @memberOf Subscriber
     *
     * @see <a href="OT.html#hasMediaProcessorSupport">OT.hasMediaProcessorSupport('audio')</a>
     *
     * @return {Promise} A promise that resolves when the operation completes successfully.
     * If there is an error, the promise is rejected and no connector is set.
     */
    this.setAudioMediaProcessorConnector = async (mediaProcessorConnector) => {
      logAnalyticsEvent('setAudioMediaProcessorConnector', 'Attempt', { message: mediaProcessorConnector ? 'setting the connector' : 'clearing the connector' });
      _audioConnector?.clearAudioMediaProcessorConnector();
      if (!mediaProcessorConnector) {
        logAnalyticsEvent('setAudioMediaProcessorConnector', 'Success', {
          message: 'clearing the connector',
        });
        return;
      }

      if (!MediaProcessorConnector.isValidConnector(mediaProcessorConnector)) {
        const message = 'Tried to set invalid MediaProcessorConnector';
        logging.error(`setAudioMediaProcessorConnector: ${message}`);
        logAnalyticsEvent('setAudioMediaProcessorConnector', 'Failure', { message });
        throw otError(
          Errors.INVALID_PARAMETER,
          new Error(`setAudioVideoMediaProcessorConnector: ${message}`)
        );
      }

      _audioConnector.initializeConnector(mediaProcessorConnector);
      _pendingSetAudioMediaProcessorConnector = true;

      const isTransitioning = amrState.isTransitioning();
      if (isTransitioning || !_isSubscribingToAudio) {
        // After the transition finishes, or when re-subscribing to audio, we will apply the
        // media processor to the audio track.
        logAnalyticsEvent('setAudioMediaProcessorConnector', 'Pending', {
          isTransitioning,
          subscribingToAudio: _isSubscribingToAudio,
        });
        return;
      }

      _audioConnector.resetAudioStreamAndTrack();

      _setAudioMediaProcessorConnector();
    };

    const _setAudioMediaProcessorConnector = async () => {
      if (!_webRTCStream) {
        // We haven't received the stream yet, this will be called again once stream is added.
        return;
      }
      try {
        await _audioConnector?.setAudioMediaProcessorConnector(_webRTCStream);
        _logPendingSetAudioMediaProcessorConnector('Success');
      } catch (err) {
        logging.error(`setAudioMediaProcessorConnector: ${err.message}`);
        _logPendingSetAudioMediaProcessorConnector('Failure', err.message);
        throw err;
      }
    };

    const _logPendingSetAudioMediaProcessorConnector = (variation, message = null) => {
      if (!_pendingSetAudioMediaProcessorConnector) return false;
      const payload = message ? { message } : null;
      logAnalyticsEvent('setAudioMediaProcessorConnector', variation, payload);
      _pendingSetAudioMediaProcessorConnector = false;
      return true;
    };

    /**
    * Dispatched when the subscriber's audio is blocked because of the
    * browser's autoplay policy.
    *
    * @see <a href="OT.html#unblockAudio">OT.unblockAudio()</a>
    * @see <a href="Subscriber.html#isAudioBlocked">Subscriber.isAudioBlocked()</a>
    * @see The <a href="#event:audioUnblocked">audioUnblocked</a> event
    * @see The <code>style.audioBlockedDisplayMode</code> property of the
    * <code>options</code> parameter of the
    * <a href="Session.html#subscribe">Session.subscribe()</a> method)
    *
    * @name audioBlocked
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the subscriber's audio is unblocked after
    * being paused because of the browser's autoplay policy.
    * <p>
    * Subscriber audio is unblocked when any of the following occurs:
    * <ul>
    *  <li>
    *    The user clicks the default Subscriber audio playback icon
    *  </li>
    *  <li>
    *     The <a href="OT.html#unblockAudio">OT.unblockAudio()</a>
    *     method is called in response to an HTML element dispatching
    *     a <code>click</code> event (if you have disabled the default
    *     audio playback icon)
    *  </li>
    *  <li>
    *     The local client gains access to the camera or microphone
    *     (for instance, in response to a successful call to
    *     <code>OT.initPublisher()</code>).
    *  </li>
    * </ul>
    *
    * @see <a href="OT.html#unblockAudio">OT.unblockAudio()</a>
    * @see The <a href="l#event:audioBlocked">audioBlocked</a> event
    * @see The <code>style.audioBlockedDisplayMode</code> property of the
    * <code>options</code> parameter of the
    * <a href="Session.html#subscribe">Session#subscribe()</a> method)
    *
    * @name audioUnblocked
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched periodically to indicate the subscriber's audio level. The event is dispatched
    * up to 60 times per second, depending on the browser. The <code>audioLevel</code> property
    * of the event is audio level, from 0 to 1.0. See {@link AudioLevelUpdatedEvent} for more
    * information.
    * <p>
    * The following example adjusts the value of a meter element that shows volume of the
    * subscriber. Note that the audio level is adjusted logarithmically and a moving average
    * is applied:
    * <pre>
    * var movingAvg = null;
    * subscriber.on('audioLevelUpdated', function(event) {
    *   if (movingAvg === null || movingAvg &lt;= event.audioLevel) {
    *     movingAvg = event.audioLevel;
    *   } else {
    *     movingAvg = 0.7 * movingAvg + 0.3 * event.audioLevel;
    *   }
    *
    *   // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
    *   var logLevel = (Math.log(movingAvg) / Math.LN10) / 1.5 + 1;
    *   logLevel = Math.min(Math.max(logLevel, 0), 1);
    *   document.getElementById('subscriberMeter').value = logLevel;
    * });
    * </pre>
    * <p>This example shows the algorithm used by the default audio level indicator displayed
    * in an audio-only Subscriber.
    *
    * @name audioLevelUpdated
    * @event
    * @memberof Subscriber
    * @see AudioLevelUpdatedEvent
    */

    /**
    * Dispatched when the video for the subscriber is disabled.
    * <p>
    * The <code>reason</code> property defines the reason the video was disabled. This can be set to
    * one of the following values:
    * <p>
    *
    * <ul>
    *
    *   <li><code>"codecNotSupported"</code> &mdash; The client's browser does not support the
    *   video codec used by the stream. For example, in Safari if you connect to a
    *   <a href="https://tokbox.com/developer/guides/create-session/#media-mode">routed session</a>
    *   in a <a href="https://tokbox.com/developer/sdks/js/safari">non-Safari Vonage Video API
    *   project</a>, and you try to subscribe to a stream that includes video, the Subscriber
    *   will dispatch a <code>videoDisabled</code> event with the <code>reason</code> property
    *   set to <code>"codecNotSupported"</code>. (In routed sessions in a non-Safari project,
    *   streams use the VP8 video codec, which is not supported in Safari.) The subscriber
    *   element will also display a "Video format not supported" message. (See
    *   <a href="OT.html#getSupportedCodecs">OT.getSupportedCodecs()</a>.)</li>
    *
    *   <li><code>"publishVideo"</code> &mdash; The publisher stopped publishing video by calling
    *   <code>publishVideo(false)</code>.</li>
    *
    *   <li><code>"quality"</code> &mdash; The Vonage Video Media Router or the publishing client
    *   stopped sending video to the subscriber based on stream quality changes due to connectivity
    *   degradation.
    *   <p>
    *   In routed sessions, the source of this event can be the Vonage Video Media Router
    *   (Subscriber Audio Fallback) or the publishing client (Publisher Audio Fallback). In both
    *   cases, the video stream is dropped specifically for the affected subscriber, while the audio
    *   stream (if present) continues uninterrupted. When the source is the publishing client, the
    *   event is followed by a <code>streamPropertyChanged</code> event dispatched by the session
    *   object, indicating that the subscribed video channel is no longer active. In general, this
    *   additional event indicates network degradation on the publisher’s side; however,
    *   subscriber-reported metrics can influence the publisher’s fallback decisions, which may
    *   complicate determining the exact source of the degradation.
    *   <p>
    *   In relayed sessions, the <code>"quality"</code> event is triggered exclusively due to the
    *   publisher’s internal audio-fallback logic (Publisher Audio Fallback). This event is not
    *   followed by a <code>streamPropertyChanged</code> event indicating that the video is inactive,
    *   as the stream may still be received by other subscribers with better network conditions.
    *   Note that in relayed sessions, although audio fallback events originate from the publisher,
    *   network degradation may be occuring on either the publisher or subscriber side.
    *   <p>
    *   Before sending this event, when the Subscriber's stream quality deteriorates to a level
    *   that is low enough that the video stream is at risk of being disabled, the Subscriber
    *   dispatches a <code>videoDisableWarning</code> event.
    *   <p>
    *   If connectivity improves to support video again, the Subscriber object dispatches
    *   a <code>videoEnabled</code> event, and the Subscriber resumes receiving video.
    *   <p>
    *   By default, the Subscriber displays a video disabled indicator when a
    *   <code>videoDisabled</code> event with this reason is dispatched and removes the indicator
    *   when the <code>videoEnabled</code> event with this reason is dispatched. You can control
    *   the display of this icon by calling the <code>setStyle()</code> method of the Subscriber,
    *   setting the <code>videoDisabledDisplayMode</code> property(or you can set the style when
    *   calling the <code>Session.subscribe()</code> method, setting the <code>style</code> property
    *   of the <code>properties</code> parameter).
    *   <p>
    *   You can control audio-only fallback behavior by setting the <code>audioFallback</code>
    *   property in the object passed to <code>OT.initPublisher()</code> on the publishing
    *   client. By default, subscriber audio fallback is enabled (except when using screen share,
    *   where it is disabled by default) and publisher audio fallback is disabled. To change this,
    *   set <code>audioFallback.subscriber</code> to <code>false</code> to disable subscriber fallback,
    *   or set <code>audioFallback.publisher</code> to <code>true</code> to enable publisher fallback.
    *   (See <a href="OT.html#initPublisher">OT.initPublisher()</a>.)
    *   </li>
    *   <li><code>"subscribeToVideo"</code> &mdash; The subscriber started or stopped subscribing to
    *   video, by calling <code>subscribeToVideo(false)</code>.
    *   </li>
    *
    * </ul>
    *
    * @see VideoEnabledChangedEvent
    * @see <a href="Subscriber.html#event:videoDisableWarning">videoDisableWarning</a> event
    * @see <a href="Subscriber.html#event:videoEnabled">videoEnabled</a> event
    * @name videoDisabled
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the Vonage Video API Media Router determines that the stream quality has degraded
    * and the video will be disabled if the quality degrades more. If the quality degrades further,
    * the Subscriber disables the video and dispatches a <code>videoDisabled</code> event.
    * <p>
    * By default, the Subscriber displays a video disabled warning indicator when this event
    * is dispatched (and the video is disabled). You can control the display of this icon by
    * calling the <code>setStyle()</code> method and setting the
    * <code>videoDisabledDisplayMode</code> property (or you can set the style when calling
    * the <code>Session.subscribe()</code> method and setting the <code>style</code> property
    * of the <code>properties</code> parameter).
    * <p>
    * This feature is only available in sessions that use the Vonage Video API Media Router (sessions with
    * the <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media mode</a>
    * set to routed), not in sessions with the media mode set to relayed.
    *
    * @see Event
    * @see <a href="Subscriber.html#event:videoDisabled">videoDisabled</a> event
    * @see <a href="Subscriber.html#event:videoDisableWarningLifted">videoDisableWarningLifted</a> event
    * @name videoDisableWarning
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the Subscriber's video element is created. Add a listener for this event when
    * you set the <code>insertDefaultUI</code> option to <code>false</code> in the call to the
    * <a href="Session.html#subscribe">Session.subscribe()</a> method. The <code>element</code>
    * property of the event object is a reference to the Subscriber's <code>video</code> element
    * (or in Internet Explorer the <code>object</code> element containing the video). Add it to
    * the HTML DOM to display the video. When you set the <code>insertDefaultUI</code> option to
    * <code>false</code>, the <code>video</code> (or <code>object</code>) element is not automatically
    * inserted into the DOM.
    * <p>
    * Add a listener for this event only if you have set the <code>insertDefaultUI</code> option to
    * <code>false</code>. If you have not set <code>insertDefaultUI</code> option to
    * <code>false</code>, do not move the <code>video</code> (or <code>object</code>) element in
    * in the HTML DOM. Doing so causes the Subscriber object to be destroyed.
    *
    * @name videoElementCreated
    * @event
    * @memberof Subscriber
    * @see VideoElementCreatedEvent
    */

    /**
    * Dispatched when the Vonage Video API Media Router determines that the stream quality has improved
    * to the point at which the video being disabled is not an immediate risk. This event is
    * dispatched after the Subscriber object dispatches a <code>videoDisableWarning</code> event.
    * <p>
    * This feature is only available in sessions that use the Vonage Video API Media Router (sessions with
    * the <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media mode</a>
    * set to routed), not in sessions with the media mode set to relayed.
    *
    * @see Event
    * @see <a href="Subscriber.html#event:videoDisableWarning">videoDisableWarning</a> event
    * @see <a href="Subscriber.html#event:videoDisabled">videoDisabled</a> event
    * @name videoDisableWarningLifted
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the MediaStream is available.
    * @see Event
    * @name mediaStreamAvailable
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the subscriber receives a caption from the transcription service.
    * You must first call the <a href="Subscriber.html#subscribeToCaptions">Subscriber.subscribeToCaptions()</a>
    * method (and the publisher of the stream must be publishing captions) before you receive
    * <code>captionReceived</code> events.
    *
    * @see <a href="CaptionReceivedEvent.html">CaptionReceivedEvent</a>
    * @see <a href="https://tokbox.com/developer/guides/live-captions/">Live Captioning</a>
    * @name captionReceived
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when a subscriber's media cannot be decrypted because the encryption secrets do not match.
    * In this scenario the user will still be subscribed to the stream, but the
    * video will be black and the audio will be silent. It is considered good practice
    * to communicate to the user why media is not viewable.
    *
    * @see Event
    * @see <a href="Subscriber.html#event:encryptionSecretMatch">encryptionSecretMatch</a> event
    * @see <a href="OT.html#initSession">OT.initSession</a>
    * @see <a href="Session.html#setEncryptionSecret">session.setEncryptionSecret</a>
    * @name encryptionSecretMismatch
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when a subscriber's media can be decrypted because the encryption secrets match.
    * This is only dispatched when there was previously a secret mismatch.
    *
    * @see Event
    * @see <a href="Subscriber.html#event:encryptionSecretMismatch">encryptionSecretMismatch</a> event
    * @see <a href="OT.html#initSession">OT.initSession</a>
    * @see <a href="Session.html#setEncryptionSecret">session.setEncryptionSecret</a>
    * @name encryptionSecretMatch
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the Vonage Video API Media Router resumes sending video to the subscriber
    * after video was previously disabled.
    * <p>
    * The <code>reason</code> property defines the reason the video was enabled. This can be set to
    * one of the following values:
    * <p>
    *
    * <ul>
    *
    *   <li><code>"codecChanged"</code> &mdash; The subscriber video was enabled after
    *   a codec change from an incompatible codec.</li>
    *
    *   <li><code>"publishVideo"</code> &mdash; The publisher started publishing video by calling
    *   <code>publishVideo(true)</code>.</li>
    *
    *   <li><code>"quality"</code> &mdash; The Vonage Video API Media Router resumed sending video
    *   to the subscriber based on stream quality changes. This feature of the Vonage Video API Media
    *   Router has a subscriber drop the video stream when connectivity degrades and then resume
    *   the video stream if the stream quality improves.
    *   <p>
    *   This feature is only available in sessions that use the Vonage Video API Media Router (sessions with
    *   the <a href="https://tokbox.com/developer/guides/create-session/#media-mode">media mode</a>
    *   set to routed), not in sessions with the media mode set to relayed.
    *   </li>
    *
    *   <li><code>"subscribeToVideo"</code> &mdash; The subscriber started or stopped subscribing to
    *   video, by calling <code>subscribeToVideo(false)</code>.
    *   </li>
    *
    * </ul>
    *
    * @see VideoEnabledChangedEvent
    * @see <a href="Subscriber.html#event:videoDisabled">videoDisabled</a> event
    * @name videoEnabled
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when a video quality interruption is detected — a pause — or a codec change is detected.
    * <p>
    * Freezes are tracked internally and exposed via statistics, but no event is triggered for each freeze due
    * to their short duration and high potential frequency.
    * <p>
    * An event is triggered only when a pause is detected (e.g., no frame rendered for 5+ seconds), as it
    * represents a more significant disruption in media playback.
    * <p>
    * This event differs from <code>videoDisabled</code> and <code>videoEnabled</code> Subscriber events in that
    * it reports video quality relevant changes. <code>videoDisabled</code> and <code>videoEnabled</code> only
    * indicates that the subscriber’s video track has been turned on or off (for example, due to a stream property
    * update or audio-fallback). In contrast, this event provides diagnostic information about quality degradation
    * causes such as freeze and pause counts and updated statistics.
    * <p>
    * This event considers all types of video interruptions lasting 5 seconds or longer. This means that, in the
    * particular case of audio fallback or stream property changes (such as video being disabled), besides triggering
    * the <code>videoEnabled</code> and <code>videoDisabled</code> events, the interruption is also recorded as a
    * pause in the video statistics, and a corresponding video interruption quality changed event will be triggered
    * once the video is restored.
    * <p>
    * The <code>reason</code> property defines the reason of the video quality change. This can be set to one of the
    * following values:
    * <p>
    *
    * <ul>
    *
    *   <li><code>"videoInterruption"</code> &mdash; Indicates that a new pause event has been detected</li>
    *
    *   <li><code>"codecChange"</code> &mdash; Indicates that the video codec has changed</li>
    *
    * </ul>
    *
    * <p>
    * The <code>stats</code> property defines an object containing a complete snapshot of the current statistics,
    * structured identically to the object returned by a <code>getStats()</code> invocation, including pauses and
    * freezes if available.
    *
    * @name videoQualityChanged
    * @event
    * @memberof Subscriber
    * @see Event
    */

    /**
    * Dispatched when a network condition change is detected for the subscriber or the remote publisher.
    * <p>
    * The current implementation triggers this event only when the network condition has changed for either side.
    * <p>
    * This event differs from <code>videoDisabled</code> and <code>videoEnabled</code> events in that it reports network
    * health changes rather than changes in the subscriber's video track state. Applications can use this event to respond
    * to network issues, such as updating UI, logging telemetry, or triggering adaptive behavior.
    * <p>
    * The properties of this event are:
    * <p>
    *
    * <ul>
    *
    *   <li><code>reason</code> &mdash; Indicates the cause of the event. Currently it will always be
    *     <code>"networkConditionChanged"</code></li>
    *
    *   <li><code>stats</code> &mdash; Object containing a complete snapshot of the current statistics, structured
    *     identically to the object returned by a <code>getStats()</code> invocation, including network condition and
    *     reason for local and remote transport/network, and the source of the network degradation.</li>
    *
    * </ul>
    *
    * @name networkConditionChanged
    * @event
    * @memberof Subscriber
    * @see Event
    */

    /**
    * Sent when the subscriber's stream has been interrupted.
    * <p>
    * In response to this event, you may want to provide a user interface notification, to let the
    * user know that the audio-video stream is temporarily disconnected and that the app is trying
    * to reconnect to it.
    * <p>
    * If the client reconnects to the stream, the Subscriber object dispatches a
    * <code>connected</code> event. Otherwise, if the client cannot reconnect to the stream,
    * the Subscriber object dispatches a <code>destroyed</code> event.
    *
    * @name disconnected
    * @event
    * @memberof Subscriber
    * @see <a href="Subscriber.html#event:connected">connected event</a>
    * @see Event
    */

    /**
    * Sent when the subscriber's stream has resumed, after the Subscriber dispatches a
    * <code>disconnected</code> event.
    *
    * @name connected
    * @event
    * @memberof Subscriber
    * @see <a href="Subscriber.html#event:disconnected">disconnected event</a>
    * @see Event
    */

    /**
    * Dispatched when the Subscriber element is removed from the HTML DOM. When this event is
    * dispatched, you may choose to adjust or remove HTML DOM elements related to the subscriber.
    * <p>
    * To prevent the Subscriber from being removed from the DOM when the stream is destroyed,
    * listen for the <a href="Session.html#event:streamDestroyed">streamDestroyed event</a>
    * dispatched by the Session object. The <code>streamDestroyed</code> event dispatched by the
    * Session object is cancelable, and calling the <code>preventDefault()</code> method of the
    * event object prevents Subscribers of the stream from being removed from the HTML DOM.
    *
    * @see Event
    * @name destroyed
    * @event
    * @memberof Subscriber
    */

    /**
    * Dispatched when the <code>stream.videoDimensions</code> property is updated by the
    * publisher. This occurs when the publisher's local video element detects a dimension change
    * and signals the update to all session participants. Examples of what can cause this:
    * <ul>
    *   <li>When <code>stream.videoType</code> is set to <code>"screen"</code> (screen-sharing)
    *       and the user resizes the captured window.</li>
    *   <li>When the video is being published by a mobile device and the user rotates the device,
    *       causing the camera orientation to change.</li>
    *   <li>When the publisher calls {@link Publisher#setPreferredResolution}, changing the
    *       target capture resolution.</li>
    * </ul>
    * This event object has a <code>newValue</code> property and an <code>oldValue</code> property,
    * representing the new and old dimensions of the video. Each of these has a <code>height</code>
    * property and a <code>width</code> property, representing the height and width, in pixels.
    * <p>
    * Note: these dimensions reflect the publisher's <em>capture</em> resolution (what the
    * publisher is encoding and sending), not the resolution actually decoded by this subscriber.
    * The decoded resolution may differ due to network adaptation or scalable video coding.
    * Use {@link Subscriber#videoWidth} and {@link Subscriber#videoHeight} for the actual
    * received resolution.
    * @name videoDimensionsChanged
    * @event
    * @memberof Subscriber
    * @see VideoDimensionsChangedEvent
    */

    /**
    * Dispatched when either the audio or video quality
    * score has changed. The scores are a heuristic value representing the expected subscriber experience. The values
    * range from 5 to 1, with the integer values corresponding to Excellent | Good | Fair | Poor | Bad.
    *
    * @name qualityScoreChanged
    * @event
    * @memberof Subscriber
    * @see QualityScoreChangedEvent
    */
    return this;
  };

  Subscriber.hasAudioOutputLevelStatCapability = hasAudioOutputLevelStatCapability;
  Subscriber.WidgetView = WidgetView;
  Subscriber.SubscriberPeerConnection = SubscriberPeerConnection;

  return Subscriber;
}
